import { useRef } from 'react';
import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  Container,
  CircularProgress,
  Alert,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  Tabs,
  Tab,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Chip,
  Divider,
  Snackbar,
  Card,
  CardContent,
  Tooltip
} from "@mui/material";

import {
  Clear as ClearIcon
} from "@mui/icons-material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  WaterDrop as WaterIcon,
  Business as BusinessIcon,
  Engineering as EngineeringIcon,
  Home as HomeIcon,
  Store as StoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from "@mui/icons-material";
import { ExecuteApiToPost, ExecutePostWithParams, GetDataFromApiWithParams,UpdateAndGetResponse } from "../../../../../services/api/ExecuteApiRequests";
import useDecodedUser from "../../../../../services/hooks/useDecodedUser";
// import AgreementCompanyList from "../../../Shared/PreRenderedComponents/AgreementCompanyList";
import companyLogo from '../../../../../assets/image/ipdc logo.png';

const initialData = {
  invoice_no: "",
  agreement_id: "",
  amount: "",
  payment_date: "",
  payment_type: "Rental",
  bank_name: "",
  reference_no: "",
  description: "",
  if_late_penality_interest_amount: "0.0",
  recieved_by: "",
  payment_period: "",
  arrear_skipped_amount: "",
  arrear_skipping_description: "",
  attachment: null
};

// Agreement types for filtering
const agreementTypes = [
  { id: "all", label: "All Agreement Types" },
  { id: "Shed Rent", label: "Shed Rent" },
  { id: "Land Leased", label: "Land Leased" },
  { id: "residential", label: "Residential" },
  { id: "commercial", label: "Commercial" }
];

// Invoice item types for different property types
const shedRentalItems = [
  { id: 'rent_payment', name: 'Rent Payment', description: 'Monthly rental for shed', unit: 'month', icon: <MoneyIcon /> },
  { id: 'stp', name: 'STP Charges', description: 'Sewage Treatment Plant charges', unit: 'month', icon: <EngineeringIcon /> },
  { id: 'etp', name: 'ETP Charges', description: 'Effluent Treatment Plant charges', unit: 'month', icon: <EngineeringIcon /> },
  { id: 'water_service', name: 'Water Service', description: 'Water supply charges', unit: 'month', icon: <WaterIcon /> },
  { id: 'management_service', name: 'Management Service', description: 'Park management fees', unit: 'month', icon: <BusinessIcon /> }
];

const landLeasedItems = [
  { id: 'leased_payment', name: 'Land Leased Payment', description: 'Yearly payment for land lease', unit: 'yearly', icon: <MoneyIcon /> },
  { id: 'water_service', name: 'Water Service', description: 'Water supply charges', unit: 'month', icon: <WaterIcon /> }
];

const residentialItems = [
  { id: 'rent_payment', name: 'Rent Payment', description: 'Monthly rental for residential property', unit: 'month', icon: <HomeIcon /> }
];

const commercialItems = [
  { id: 'rent_payment', name: 'Rent Payment', description: 'Monthly rental for commercial property', unit: 'month', icon: <StoreIcon /> }
];

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Helper function to get date 30 days from now
const getDueDate = () => {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 15);
  return dueDate.toISOString().split('T')[0];
};

// Function to get items based on property type
const getItemsByPropertyType = (propertyType) => {
  const normalizedType = propertyType?.toString().toLowerCase().trim();
  console.log("Getting items for property type:", normalizedType);
  
  // Check for shed-related types
  if (normalizedType.includes('shed') || 
      normalizedType === 'shed rent' || 
      normalizedType === 'shed rental' ||
      normalizedType === 'shedrent') {
    console.log("Using shed rental items (5 items)");
    return shedRentalItems;
  }
  
  // Check for land-related types
  if (normalizedType.includes('land') || 
      normalizedType.includes('lease') || 
      normalizedType === 'developed land lease' || 
      normalizedType === 'land leased' ||
      normalizedType === 'leased land') {
    console.log("Using land leased items (2 items)");
    return landLeasedItems;
  }
  
  // Check for residential
  if (normalizedType.includes('residential') || 
      normalizedType.includes('residence') || 
      normalizedType === 'residential') {
    console.log("Using residential items (1 item)");
    return residentialItems;
  }
  
  // Check for commercial
  if (normalizedType.includes('commercial') || 
      normalizedType.includes('shop') || 
      normalizedType.includes('office') || 
      normalizedType === 'commercial') {
    console.log("Using commercial items (1 item)");
    return commercialItems;
  }
  
  // Default
  console.warn(`Unknown property type: "${propertyType}" - Defaulting to residential items`);
  return residentialItems;
};

// Function to get initial invoice data based on property type
const getInitialInvoiceData = (propertyType = "Shed Rent") => ({
  id: "",
  invoice_number: "",
  investor_id: "",
  investor_name: "",
  property_type: propertyType,
  property_id: "",
  invoice_date: getTodayDate(),
  due_date: getDueDate(),
  items: getItemsByPropertyType(propertyType).map(item => ({
    ...item,
    quantity: item.id === 'rent_payment' || item.id === 'leased_payment' || item.id === 'management_service' ? 1 : 0,
    unit_price: 0,
    amount: 0
  })),
  subtotal: 0,
  tax_rate: 0,
  tax_amount: 0,
  discount: 0,
  arrear: 0,
  total_amount: 0,
  status: "pending",
  notes: "",
  park_name: "",
  payment_records: []
});

// Create a TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`invoice-tabpanel-${index}`}
      aria-labelledby={`invoice-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Mock data for investors
const mockInvestors = [
  { id: "INV-001", name: "Tech Solutions Ltd", property_id: "SHED-001", ownership_type: "Shed Rental", tin_no: "TIN001", payment_mode: "Bank Transfer" },
  { id: "INV-002", name: "Manufacturing Corp", property_id: "LAND-001", ownership_type: "Developed Land Leased", tin_no: "TIN002", payment_mode: "Cash" },
  { id: "INV-003", name: "Industrial Works", property_id: "SHED-002", ownership_type: "Shed Rental", tin_no: "TIN003", payment_mode: "Bank Transfer" },
  { id: "INV-004", name: "Global Enterprises", property_id: "LAND-002", ownership_type: "Developed Land Leased", tin_no: "TIN004", payment_mode: "Check" },
  { id: "INV-005", name: "Innovation Hub", property_id: "SHED-003", ownership_type: "Shed Rental", tin_no: "TIN005", payment_mode: "Bank Transfer" },
  { id: "INV-006", name: "John Smith", property_id: "APT-101", ownership_type: "Residential", tin_no: "TIN006", payment_mode: "Cash" },
  { id: "INV-007", name: "Sarah Johnson", property_id: "APT-202", ownership_type: "Residential", tin_no: "TIN007", payment_mode: "Bank Transfer" },
  { id: "INV-008", name: "Retail Store Inc", property_id: "SHOP-001", ownership_type: "Commercial", tin_no: "TIN008", payment_mode: "Check" },
  { id: "INV-009", name: "Office Space Ltd", property_id: "OFFICE-101", ownership_type: "Commercial", tin_no: "TIN009", payment_mode: "Bank Transfer" }
];

// Mock data for demonstration
const mockInvoices = [
  {
    id: "1",
    invoice_num: "INV-202402-001",
    investor_id: "INV-001",
    investor_name: "Tech Solutions Ltd",
    property_type: "Shed Rental",
    property_id: "SHED-001",
    invoice_date: "2024-02-01",
    due_date: "2024-03-03",
    items: shedRentalItems.map(item => ({
      ...item,
      quantity: 1,
      unit_price: 1000,
      amount: 1000
    })),
    subtotal: 5000,
    tax_rate: 10,
    tax_amount: 500,
    discount: 0,
    arrear: 0,
    total_amount: 5500,
    status: "pending",
    notes: "Monthly invoice for February 2024",
    payment_records: [],
    recorded_by: "admin",
    tin_no: "TIN001",
    payment_mode: "Bank Transfer",
    park_name: "Industrial Park 1"
  },
  {
    id: "2",
    invoice_num: "INV-202402-002",
    investor_id: "INV-002",
    investor_name: "Manufacturing Corp",
    property_type: "Developed Land Leased",
    property_id: "LAND-001",
    invoice_date: "2024-02-01",
    due_date: "2024-03-03",
    items: landLeasedItems.map(item => ({
      ...item,
      quantity: 1,
      unit_price: 1500,
      amount: 1500
    })),
    subtotal: 3000,
    tax_rate: 10,
    tax_amount: 300,
    discount: 500,
    arrear: 200,
    total_amount: 3000,
    status: "paid",
    notes: "Yearly invoice for land lease",
    payment_records: [
      {
        id: "PAY-001",
        amount: 2800,
        payment_date: "2024-02-15",
        payment_method: "bank_transfer",
        transaction_id: "TRX-20240215-001",
        reference: "BNK20240215001",
        status: "completed",
        notes: "Full payment received"
      }
    ],
    recorded_by: "admin",
    tin_no: "TIN002",
    payment_mode: "Cash",
    park_name: "Industrial Park 1"
  },
  {
    id: "3",
    invoice_num: "INV-202402-003",
    investor_id: "INV-006",
    investor_name: "John Smith",
    property_type: "Residential",
    property_id: "APT-101",
    invoice_date: "2024-02-01",
    due_date: "2024-03-03",
    items: residentialItems.map(item => ({
      ...item,
      quantity: 1,
      unit_price: 800,
      amount: 800
    })),
    subtotal: 800,
    tax_rate: 5,
    tax_amount: 40,
    discount: 0,
    arrear: 0,
    total_amount: 840,
    status: "pending",
    notes: "Monthly rent for apartment",
    payment_records: [],
    recorded_by: "admin",
    tin_no: "TIN006",
    payment_mode: "Cash",
    park_name: "Residential Park"
  },
  {
    id: "4",
    invoice_num: "INV-202402-004",
    investor_id: "INV-008",
    investor_name: "Retail Store Inc",
    property_type: "Commercial",
    property_id: "SHOP-001",
    invoice_date: "2024-02-01",
    due_date: "2024-03-03",
    items: commercialItems.map(item => ({
      ...item,
      quantity: 1,
      unit_price: 1200,
      amount: 1200
    })),
    subtotal: 1200,
    tax_rate: 8,
    tax_amount: 96,
    discount: 100,
    arrear: 0,
    total_amount: 1196,
    status: "paid",
    notes: "Monthly rent for retail shop",
    payment_records: [
      {
        id: "PAY-002",
        amount: 1196,
        payment_date: "2024-02-10",
        payment_method: "check",
        transaction_id: "TRX-20240210-001",
        reference: "CHK20240210001",
        status: "completed",
        notes: "Rent payment for February"
      }
    ],
    recorded_by: "admin",
    tin_no: "TIN008",
    payment_mode: "Check",
    park_name: "Commercial Park"
  }
];

export default function IndustrialParkInvoiceManagement() {
  // Add refs for different printable sections
  const searchResultsRef = useRef(null);
  const detailViewRef = useRef(null);
  const editViewRef = useRef(null);
  
  // Main state
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(0);
  const [paymentType, setPaymentType] = useState(initialData.payment_type);
  const [mainTabValue, setMainTabValue] = useState(0);
  const [formData, setFormData] = useState(getInitialInvoiceData());
  const [isLoading, setIsLoading] = useState(false);
  const [agreementDetail, setAgreementDetail] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [selectedInvesters, setSelectedInvesters] = useState({});
  const [tariff, setTariff] = useState("1.5");
  const [arrearInterest, setArrearInterest] = useState("0");
  
  // NEW: Agreement type filter state
  const [selectedAgreementType, setSelectedAgreementType] = useState("all");
  const [filteredInvestors, setFilteredInvestors] = useState([]);
  
  // Inline editing states
  const [editingId, setEditingId] = useState(null);
  const [editedRowData, setEditedRowData] = useState(null);
  const [originalRowData, setOriginalRowData] = useState(null);
  
  // Toast Notification States
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  
  // Search and edit states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // ==================== STATE DECLARATIONS ====================
const [allInvoices, setAllInvoices] = useState([]); // Store all fetched invoices

  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [detailFlag, setDetailFlag] = useState(false);
  
  // Investor list state
  const [investors, setInvestor] = useState([]);
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    payment_date: getTodayDate(),
    payment_method: "bank_transfer",
    transaction_id: "",
    reference: "",
    notes: ""
  });
  const [users, setUsers] = useState([]);
  const decodedUser = useDecodedUser();
  const userValuesArray = Object.values(decodedUser || {});
  
  const mainTabs = ["Generate Invoice", "Edit Invoice", "View Invoice & Payments"];

  // Function to fetch agreements based on selected type
  const fetchAgreementsByType = async (type) => {
    try {
      setIsLoading(true);
      
      console.log(`Fetching agreements for type: ${type}`);
      
      // Pass the agreement type as a query parameter to the API
      const agreData = await GetDataFromApiWithParams(
        "agreements/getagreementbytype", 
        { agreement_type: type }
      );
      
      console.log(`AGREEMENTS for type ${type}:`, agreData);
      
      if (agreData && Array.isArray(agreData)) {
        // Remove duplicates if any
        const uniqueAgreData = [...new Map(agreData.map(item => [item.id, item])).values()];
        setInvestor(uniqueAgreData);
        setFilteredInvestors(uniqueAgreData);
      } else {
        setInvestor([]);
        setFilteredInvestors([]);
      }
    } catch (error) {
      console.error("Error fetching agreements:", error);
      setInvestor([]);
      setFilteredInvestors([]);
      showErrorToast("Failed to fetch agreements");
    } finally {
      setIsLoading(false);
    }
  };
  const fetchAgreementsByType2 = async (type) => {
    try {
      setIsLoading(true);
      
      console.log(`Fetching agreements for type: ${type}`);
      
      // Pass the agreement type as a query parameter to the API
      const agreData = await GetDataFromApiWithParams(
        "agreements/getagreementbytype", 
        { agreement_type: type }
      );

      console.log("TYPE TYPE", type)
      
      console.log(`AGREEMENTS for type ${type}:`, agreData);
      
      if (agreData && Array.isArray(agreData)) {
        // Remove duplicates if any
        const uniqueAgreData = [...new Map(agreData.map(item => [item.id, item])).values()];
        setInvestor(uniqueAgreData);
        setFilteredInvestors(uniqueAgreData);
      } else {
        setInvestor([]);
        setFilteredInvestors([]);
      }
    } catch (error) {
      console.error("Error fetching agreements:", error);
      setInvestor([]);
      setFilteredInvestors([]);
      showErrorToast("Failed to fetch agreements");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle agreement type change - this will trigger data fetch
  const handleAgreementTypeChange2 = (e) => {
    const type = e.target.value;
    console.log("AGREEMENT TYPE:", type)
    setSelectedAgreementType(type);
    
    // Reset investor selection when type changes
    setFormData(prev => ({
      ...prev,
      investor_id: "",
      investor_name: "",
      id: "",
      tin_no: "",
      payment_mode: "",
      ownership_type: "",
      park_name: ""
    }));
    
    // Fetch agreements for the selected type
    fetchAgreementsByType2(type);
  };

  // Custom print function that actually works
  const printContent = (element) => {
    if (!element) {
      showErrorToast("Nothing to print!");
      return;
    }

    // Get all styles from the document
    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          const cssRules = styleSheet.cssRules || styleSheet.rules;
          if (cssRules) {
            return Array.from(cssRules).map((rule) => rule.cssText).join('');
          }
        } catch (e) {
          return '';
        }
        return '';
      })
      .join('');

    // Get the HTML content
    const content = element.cloneNode(true);
    
    // Remove any elements with no-print class from the cloned content
    const noPrintElements = content.querySelectorAll('.no-print');
    noPrintElements.forEach(el => el.remove());

    // Create a new window
    const printWindow = window.open('', '_blank');
    
    // Write the content with styles
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print</title>
          <style>
            ${styles}
            @media print {
              body { 
                padding: 20px; 
                font-family: Arial, sans-serif;
              }
              table { 
                border-collapse: collapse; 
                width: 100%;
              }
              th, td { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: left;
              }
              th { 
                background-color: #f2f2f2;
              }
              .MuiPaper-root {
                box-shadow: none !important;
              }
            }
          </style>
        </head>
        <body>
          ${content.outerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Print after a short delay to ensure content is loaded
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    }, 500);
  };

  // Print handlers
  const handlePrintSearchResults = () => {
    if (searchResultsRef.current) {
      printContent(searchResultsRef.current);
      showInfoToast("Printing search results...");
    } else {
      showErrorToast("No search results to print");
    }
  };

  const handlePrintDetailView = () => {
    if (detailViewRef.current) {
      printContent(detailViewRef.current);
      showInfoToast("Printing invoice...");
    } else {
      showErrorToast("No invoice details to print");
    }
  };

  const handlePrintEditView = () => {
    if (editViewRef.current) {
      printContent(editViewRef.current);
      showInfoToast("Printing edit list...");
    } else {
      showErrorToast("No edit list to print");
    }
  };

  const handlePrintCurrentView = () => {
    if (mainTabValue === 1) {
      handlePrintEditView();
    } else if (mainTabValue === 2) {
      if (detailFlag && selectedInvoice) {
        handlePrintDetailView();
      } else {
        handlePrintSearchResults();
      }
    }
  };

  // Download handler for current view
  const handleDownloadCurrentView = async () => {
    if (mainTabValue === 1) {
      await generateTablePDF(editViewRef.current, 'Invoice-Edit-List');
    } else if (mainTabValue === 2) {
      if (detailFlag && selectedInvoice) {
        await generateInvoicePDF(selectedInvoice, selectedInvoice.invoice_number);
      } else {
        await generateTablePDF(searchResultsRef.current, 'Invoice-Search-Results');
      }
    }
  };

  // Generate PDF from table
  const generateTablePDF = async (contentRef, filename) => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;
      
      if (!contentRef) {
        showErrorToast("No content to download");
        return;
      }
      
      const canvas = await html2canvas(contentRef, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for tables
      const imgWidth = 280;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`${filename}.pdf`);
      
      showSuccessToast("Document downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      showErrorToast("Failed to generate PDF!");
    }
  };

  // Toast Notification Functions
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  const showSuccessToast = (message) => showToast(message, "success");
  const showErrorToast = (message) => showToast(message, "error");
  const showWarningToast = (message) => showToast(message, "warning");
  const showInfoToast = (message) => showToast(message, "info");

  // Calculate totals - ARREAR IS ADDED, DISCOUNT IS DEDUCTED
const calculateTotals = (items, taxRate = 0, discount = 0, arrear = 0) => {
  // Handle empty strings or non-numeric values
  const safeTaxRate = taxRate === '' ? 0 : parseFloat(taxRate) || 0;
  const safeDiscount = discount === '' ? 0 : parseFloat(discount) || 0;
  const safeArrear = arrear === '' ? 0 : parseFloat(arrear) || 0;
  
  // Calculate subtotal from items - ensure we're using the actual item amounts
  const subtotal = items.reduce((sum, item) => {
    // Make sure amount is a number
    const itemAmount = typeof item.amount === 'number' ? item.amount : parseFloat(item.amount) || 0;
    console.log(`Item ${item.name} amount:`, itemAmount);
    return sum + itemAmount;
  }, 0);
  
  const taxAmount = (subtotal * safeTaxRate) / 100;
  const total = subtotal + taxAmount - safeDiscount + safeArrear;
  
  console.log("Calculated subtotal:", subtotal);
  console.log("Calculated total:", total);
  
  return { subtotal, taxAmount, total };
};

  // Safe number formatter for display - use this everywhere instead of .toFixed()
  const safeToFixed = (value, decimals = 2) => {
    if (value === '' || value === null || value === undefined) return '0.00';
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  // Handle item changes with auto-calculation
const handleItemChange = (index, field, value) => {
  const updatedItems = [...formData.items];
  
  // Handle the '0' concatenation issue
  if (value === '' || value === '0') {
    updatedItems[index][field] = value === '' ? '' : value;
  } else {
    updatedItems[index][field] = parseFloat(value) || 0;
  }
  
  // Calculate amount based on quantity and unit price
  const quantity = updatedItems[index].quantity;
  const unitPrice = updatedItems[index].unit_price;
  
  if (quantity !== '' && unitPrice !== '' && quantity !== undefined && unitPrice !== undefined) {
    updatedItems[index].amount = (parseFloat(quantity) || 0) * (parseFloat(unitPrice) || 0);
  } else {
    updatedItems[index].amount = 0;
  }
  
  // Recalculate all totals
  const subtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  const taxRate = parseFloat(formData.tax_rate) || 0;
  const taxAmount = (subtotal * taxRate) / 100;
  const discount = parseFloat(formData.discount) || 0;
  const arrear = parseFloat(formData.arrear) || 0;
  const total = subtotal + taxAmount - discount + arrear;
  
  setFormData({
    ...formData,
    items: updatedItems,
    subtotal: subtotal,
    tax_amount: taxAmount,
    total_amount: total
  });
};

  // Function to calculate all item amounts and update state
  const updateItemAmounts = () => {
    if (formData.investor_id && formData.items.length > 0) {
      const selectedInvestor = Array.isArray(investors) 
        ? investors.find(inv => inv.id === formData.investor_id) 
        : null;
      
      if (selectedInvestor) {
        let needsUpdate = false;
        const updatedItems = formData.items.map((item, index) => {
          const newItem = { ...item };
          
          // Update quantity for Rent Payment and Management Service
          if (index === 0 || index === 4) {
            const newQuantity = selectedInvestor?.area_on_deed || 1;
            if (newItem.quantity !== newQuantity) {
              newItem.quantity = newQuantity;
              needsUpdate = true;
            }
          }
          
          // Update unit price for Rent Payment
          if (index === 0) {
            const newUnitPrice = parseFloat(tariff) || 0;
            if (newItem.unit_price !== newUnitPrice) {
              newItem.unit_price = newUnitPrice;
              needsUpdate = true;
            }
          }
          
          // Update unit price for Management Service - USING ANNUAL MANAGEMENT FEE / 12
          if (index === 4) {
            const annualFee = parseFloat(selectedInvestor?.annual_mngmnt_service_fee) || 0;
            const newUnitPrice = annualFee / 12;
            if (newItem.unit_price !== newUnitPrice) {
              newItem.unit_price = newUnitPrice;
              needsUpdate = true;
            }
          }
          
          // Recalculate amount for non-editable fields only
          if (index === 0 || index === 4) {
            const newAmount = (newItem.quantity || 0) * (newItem.unit_price || 0);
            if (newItem.amount !== newAmount) {
              newItem.amount = newAmount;
              needsUpdate = true;
            }
          }
          
          return newItem;
        });
        
        if (needsUpdate) {
          const totals = calculateTotals(
            updatedItems, 
            formData.tax_rate, 
            formData.discount,
            formData.arrear || 0
          );
          
          setFormData(prev => ({
            ...prev,
            items: updatedItems,
            subtotal: totals.subtotal,
            tax_amount: totals.taxAmount,
            total_amount: totals.total
          }));
        }
      }
    }
  };

  // Handle form field changes - FIXED VERSION
const handleChange = (e) => {
  const { name, value } = e.target;
  
  if (name === 'tax_rate' || name === 'discount' || name === 'arrear') {
    // Handle empty string or just '0' - set to empty to avoid concatenation
    let numericValue;
    
    if (value === '' || value === '0') {
      // If user is typing something new, don't prepend '0'
      numericValue = value === '' ? '' : value;
    } else {
      // Parse the full number
      numericValue = parseFloat(value) || 0;
    }
    
    // Only calculate totals if we have a valid number
    if (numericValue !== '') {
      const totals = calculateTotals(
        formData.items, 
        name === 'tax_rate' ? numericValue : formData.tax_rate,
        name === 'discount' ? numericValue : formData.discount,
        name === 'arrear' ? numericValue : (formData.arrear || 0)
      );
      
      setFormData(prev => ({
        ...prev,
        [name]: numericValue,
        subtotal: totals.subtotal,
        tax_amount: totals.taxAmount,
        total_amount: totals.total
      }));
    } else {
      // Just update the field value without recalculating totals
      setFormData(prev => ({
        ...prev,
        [name]: value // This will be the new digit or empty string
      }));
    }
  } else if (name === 'agreement_id') {
    console.log("Selected agreement ID:", value);
    
    const selectedInvestor = investors.find(inv => inv.id === value);
    setSelectedInvesters(selectedInvestor);
    
    if (!selectedInvestor) {
      console.log("No investor found for ID:", value);
      return;
    }
    
    // Get the original ownership type from the investor
    const originalOwnershipType = selectedInvestor.ownership_type || "Residential";
    console.log("Original ownership type:", originalOwnershipType);
    console.log("Investor data:", selectedInvestor);
    console.log("Monthly rent from investor:", selectedInvestor.monthly_rent);
    
    // Map the ownership type to match the items list
    let itemsType = "Residential"; // Default
    
    if (originalOwnershipType) {
      const typeLower = originalOwnershipType.toString().toLowerCase().trim();
      
      // Check for shed-related types
      if (typeLower.includes('shed') || typeLower === 'shed rent' || typeLower === 'shed rental') {
        itemsType = "Shed Rental";
      }
      // Check for land-related types
      else if (typeLower.includes('land') || typeLower.includes('lease') || 
               typeLower === 'developed land lease' || typeLower === 'land leased' || 
               typeLower === 'land leased') {
        itemsType = "Land Leased";
      }
      // Check for residential
      else if (typeLower.includes('residential') || typeLower.includes('residence') || 
               typeLower === 'residential') {
        itemsType = "Residential";
      }
      // Check for commercial
      else if (typeLower.includes('commercial') || typeLower.includes('shop') || 
               typeLower.includes('office') || typeLower === 'commercial') {
        itemsType = "Commercial";
      }
    }
    
    console.log("Mapped items type:", itemsType);
    
    // Get the appropriate items based on property type
    const baseItems = getItemsByPropertyType(itemsType);
    console.log("Base items:", baseItems);
    
    // Determine if this is Residential or Commercial
    const isResidentialOrCommercial = itemsType === "Residential" || itemsType === "Commercial";
    
    // Create new items array with proper values
    const updatedItems = baseItems.map((item, index) => {
      const newItem = { ...item, amount: 0, quantity: 0, unit_price: 0 };
      
      // For Rent Payment (first item in all types)
      if (index === 0) {
        if (isResidentialOrCommercial) {
          // For Residential/Commercial, use monthly_rent
          newItem.quantity = 1;
          newItem.unit_price = parseFloat(selectedInvestor.monthly_rent) || 0;
          console.log(`Setting Residential/Commercial rent: quantity=1, unit_price=${newItem.unit_price}`);
        } else {
          // For Shed/Land, use area_on_deed and tariff
          newItem.quantity = parseFloat(selectedInvestor.area_on_deed) || 1;
          newItem.unit_price = parseFloat(tariff) || 0;
          console.log(`Setting Shed/Land rent: quantity=${newItem.quantity}, unit_price=${newItem.unit_price}`);
        }
        // Calculate amount immediately
        newItem.amount = newItem.quantity * newItem.unit_price;
      }
      
      // For Management Service (index 4) - only for Shed/Land
      if (index === 4 && !isResidentialOrCommercial) {
        newItem.quantity = parseFloat(selectedInvestor.area_on_deed) || 1;
        const annualFee = parseFloat(selectedInvestor.annual_mngmnt_service_fee) || 0;
        newItem.unit_price = annualFee / 12;
        newItem.amount = newItem.quantity * newItem.unit_price;
        console.log(`Setting Management Service: quantity=${newItem.quantity}, unit_price=${newItem.unit_price}, amount=${newItem.amount}`);
      }
      
      return newItem;
    });
    
    console.log("Updated items:", updatedItems);
    
    // Calculate subtotal from items
    const subtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    console.log("Calculated subtotal from items:", subtotal);
    
    // Create new form data
    const newFormData = {
      invoice_no: "",
      agreement_id: value,
      amount: "",
      payment_date: "",
      payment_type: "Rental",
      bank_name: "",
      reference_no: "",
      description: "",
      if_late_penality_interest_amount: "0.0",
      recieved_by: "",
      payment_period: "",
      arrear_skipped_amount: "",
      arrear_skipping_description: "",
      attachment: null,
      invoice_number: "",
      investor_id: value,
      id: selectedInvestor.id || "",
      tin_no: selectedInvestor.tin_no || "",
      payment_mode: selectedInvestor.payment_mode || "N/A",
      ownership_type: originalOwnershipType,
      park_name: selectedInvestor.park_name || "",
      monthly_rent: selectedInvestor.monthly_rent || 0,
      investor_name: selectedInvestor.company_name || selectedInvestor.investor_name || selectedInvestor.name || "",
      property_id: selectedInvestor.property_id || "",
      area_on_deed: selectedInvestor.area_on_deed || 1,
      property_type: itemsType,
      invoice_date: getTodayDate(),
      due_date: getDueDate(),
      items: updatedItems,
      subtotal: subtotal,
      tax_rate: 0,
      tax_amount: 0,
      discount: 0,
      arrear: 0,
      total_amount: subtotal, // Start with subtotal as total
      status: "pending",
      notes: "",
      payment_records: []
    };
    
    console.log("Final form data:", {
      property_type: newFormData.property_type,
      monthly_rent: newFormData.monthly_rent,
      subtotal: newFormData.subtotal,
      items: newFormData.items.map(item => ({
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        amount: item.amount
      }))
    });
    
    setFormData(newFormData);
    
    // Fetch additional data if needed (for Shed/Land types)
    if (!isResidentialOrCommercial && selectedInvestor.grace_period_ending_date) {
      const [day, month, year] = selectedInvestor.grace_period_ending_date.split('-');
      const handoverDate = new Date(year, month - 1, day);
      const currentDate = new Date();
      const timeDifference = currentDate - handoverDate;
      const payment_period = timeDifference / (1000 * 60 * 60 * 24 * 365.25);
      const parkId = selectedInvestor.park_id;
      const agreeId = selectedInvestor.id;
      
      const fetchAgreements = async () => {
        try {
          const response = await GetDataFromApiWithParams("finances/payementperiod", {
            payment_period: payment_period,
            parkId: parkId
          });
          //alert(payment_period)
          if(payment_period<0)
          {
            
          showInfoToast(`Before Grace Period, payment not begin, Grace period ends ${selectedInvestor.grace_period_ending_date}.`);
          
        }
          console.log("Payment period response:", response);
          if (response && response[0]?.amount) {
            setTariff(response[0].amount);
            console.log("New tariff XXXXXXXXXXXXXXXXXXXx:", response[0].amount);
            
            // Update form data with new tariff
            setFormData(prev => {
              const updatedItems = [...prev.items];
              if (updatedItems[0]) {
                updatedItems[0].unit_price = parseFloat(response[0].amount) || 0;
                updatedItems[0].amount = updatedItems[0].quantity * updatedItems[0].unit_price;
              }
              
              const newSubtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
              const newTaxAmount = (newSubtotal * (parseFloat(prev.tax_rate) || 0)) / 100;
              const newTotal = newSubtotal + newTaxAmount - (parseFloat(prev.discount) || 0) + (parseFloat(prev.arrear) || 0);
              
              console.log("Updated after tariff change:", {
                subtotal: newSubtotal,
                total: newTotal
              });
              
              return {
                ...prev,
                items: updatedItems,
                subtotal: newSubtotal,
                tax_amount: newTaxAmount,
                total_amount: newTotal
              };
            });
          }
          
          const response2 = await GetDataFromApiWithParams("finances/arrearinterest", {
            agreement_id: agreeId,
          });
          
          if (response2 && response2[0]) {
            const arrearAmount = parseFloat(response2[0].arrear_amount) || 0;
            const interest = parseFloat(response2[0].interest) || 0;
            const arrint = arrearAmount + interest;
            
            setArrearInterest(String(arrint));
            console.log("Arrear interest:", arrint);
            
            // Update form data with arrear value
            setFormData(prev => {
              const newArrear = arrint;
              const newTotal = prev.subtotal + (prev.tax_amount || 0) - (parseFloat(prev.discount) || 0) + newArrear;
              
              console.log("Updated after arrear change:", {
                arrear: newArrear,
                total: newTotal
              });
              
              return {
                ...prev,
                arrear: newArrear,
                total_amount: newTotal
              };
            });
          }
        } catch (error) {
          console.error("Error fetching agreements:", error);
        }
      };
      fetchAgreements();
    }
  } else if (name === 'invoice_date' || name === 'due_date' || name === 'notes' || name === 'invoice_number') {
    // Handle date and text fields
    setFormData(prev => ({ ...prev, [name]: value }));
  } else {
    // Handle any other fields
    setFormData(prev => ({ ...prev, [name]: value }));
  }
};

useEffect(() => {
  // console.log("INTEREST INTEREST")
  updateArrearInterest()
}, [formData.investor_id]);
// Add this useEffect to ensure amounts are recalculated when items change
useEffect(() => {
  // If we have items and form data, ensure all amounts are calculated correctly
  if (formData.items && formData.items.length > 0) {
    // Check if this is Residential or Commercial
    const isResidentialOrCommercial = 
      formData.property_type?.toLowerCase().includes('residential') || 
      formData.property_type?.toLowerCase().includes('commercial');
    
    if (isResidentialOrCommercial) {
      // For Residential/Commercial, ensure the rent payment is calculated correctly
      const updatedItems = [...formData.items];
      let needsUpdate = false;
      
      updatedItems.forEach((item, index) => {
        if (index === 0) { // Rent Payment item
          const quantity = parseFloat(item.quantity) || 1;
          const unitPrice = parseFloat(item.unit_price) || 0;
          const calculatedAmount = quantity * unitPrice;
          
          if (Math.abs(item.amount - calculatedAmount) > 0.01) {
            updatedItems[index].amount = calculatedAmount;
            needsUpdate = true;
          }
        }
      });
      
      if (needsUpdate) {
        const newSubtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
        const taxRate = parseFloat(formData.tax_rate) || 0;
        const taxAmount = (newSubtotal * taxRate) / 100;
        const discount = parseFloat(formData.discount) || 0;
        const arrear = parseFloat(formData.arrear) || 0;
        const total = newSubtotal + taxAmount - discount + arrear;
        
        setFormData(prev => ({
          ...prev,
          items: updatedItems,
          subtotal: newSubtotal,
          tax_amount: taxAmount,
          total_amount: total
        }));
      }
    }
  }
}, [formData.items, formData.property_type, formData.tax_rate, formData.discount, formData.arrear]);

// Add this useEffect to ensure amounts are displayed correctly when tariff or investor changes

useEffect(() => {
  if (formData.investor_id && formData.items.length > 0) {
    const selectedInvestor = Array.isArray(investors) 
      ? investors.find(inv => inv.id === formData.investor_id) 
      : null;
    
    if (selectedInvestor) {
      const isResidentialOrCommercial = 
        formData.property_type?.toLowerCase().includes('residential') || 
        formData.property_type?.toLowerCase().includes('commercial');
      
      let needsUpdate = false;
      const updatedItems = [...formData.items];
      
      updatedItems.forEach((item, index) => {
        if (index === 0) { // Rent Payment
          if (isResidentialOrCommercial) {
            // For Residential/Commercial, ensure monthly rent is used
            const newUnitPrice = parseFloat(selectedInvestor.monthly_rent) || 0;
            if (item.unit_price !== newUnitPrice) {
              item.unit_price = newUnitPrice;
              needsUpdate = true;
            }
            // Quantity should always be 1 for Residential/Commercial
            if (item.quantity !== 1) {
              item.quantity = 1;
              needsUpdate = true;
            }
          } else {
            // For Shed/Land, use area_on_deed and tariff
            const newQuantity = selectedInvestor?.area_on_deed || 1;
            if (item.quantity !== newQuantity) {
              item.quantity = newQuantity;
              needsUpdate = true;
            }
            const newUnitPrice = parseFloat(tariff) || 0;
            if (item.unit_price !== newUnitPrice) {
              item.unit_price = newUnitPrice;
              needsUpdate = true;
            }
          }
          
          // Recalculate amount
          const newAmount = (item.quantity || 0) * (item.unit_price || 0);
          if (item.amount !== newAmount) {
            item.amount = newAmount;
            needsUpdate = true;
          }
        }
        
        // For Management Service (index 4) - only for Shed/Land
        if (index === 4 && !isResidentialOrCommercial) {
          const newQuantity = selectedInvestor?.area_on_deed || 1;
          if (item.quantity !== newQuantity) {
            item.quantity = newQuantity;
            needsUpdate = true;
          }
          const annualFee = parseFloat(selectedInvestor?.annual_mngmnt_service_fee) || 0;
          const newUnitPrice = annualFee / 12;
          if (item.unit_price !== newUnitPrice) {
            item.unit_price = newUnitPrice;
            needsUpdate = true;
          }
          
          // Recalculate amount
          const newAmount = (item.quantity || 0) * (item.unit_price || 0);
          if (item.amount !== newAmount) {
            item.amount = newAmount;
            needsUpdate = true;
          }
        }
      });
      
      if (needsUpdate) {
        const newSubtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
        const taxRate = parseFloat(formData.tax_rate) || 0;
        const taxAmount = (newSubtotal * taxRate) / 100;
        const discount = parseFloat(formData.discount) || 0;
        const arrear = parseFloat(formData.arrear) || 0;
        const total = newSubtotal + taxAmount - discount + arrear;
        
        setFormData(prev => ({
          ...prev,
          items: updatedItems,
          subtotal: newSubtotal,
          tax_amount: taxAmount,
          total_amount: total
        }));
      }
    }
  }
}, [tariff, formData.investor_id, investors, formData.property_type]);

  // useEffect to update item amounts when tariff or investor changes
  useEffect(() => {
    updateItemAmounts();
  }, [tariff, formData.investor_id, investors]);


  // useEffect to update totals when arrearInterest changes
  useEffect(() => {
    if (formData.investor_id) {
      const arrearValue = parseFloat(arrearInterest) || 0;
      const totals = calculateTotals(
        formData.items,
        formData.tax_rate,
        formData.discount,
        arrearValue
      );
      
      setFormData(prev => ({
        ...prev,
        arrear: arrearValue,
        subtotal: totals.subtotal,
        tax_amount: totals.taxAmount,
        total_amount: totals.total
      }));
    }
  }, [arrearInterest]);

  useEffect(() => {
    if (formData.investor_id) {
      console.log("FormData updated:", {
        property_type: formData.property_type,
        monthly_rent: formData.monthly_rent,
        items: formData.items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount
        })),
        subtotal: formData.subtotal,
        total: formData.total_amount
      });
    }
  }, [formData]);
  const updateArrearInterest = async () => {
    try {
      const agreement=formData.investor_id
            
      const arrear=await GetDataFromApiWithParams("finances/arrearinterest", {
        agreement_id: agreement,
      });
      
      console.log("ARREAR ARREAR",arrear)
      
      if (arrear) {
        console.log("KKKKKKKKKkkkkkkk")
        const responseData = await UpdateAndGetResponse("finances/updatearrear/arrear", {agreement_id: agreement});
  
       }
    } catch (error) {
      console.error("Error fetching agreements:", error);
     
     
      showErrorToast("Failed to fetch agreements");
    } finally {
      
    }
  };
  // Generate invoice number
  const generateInvoiceNumber = async () => {
    try {
      const response = await GetDataFromApiWithParams("finances/invoicecount", []);
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const count = response && response[0] ? parseInt(response[0].count) + 1 : 1;
      return `INV-${year}${month}-${count}`;
    } catch (error) {
      console.error("Error generating invoice number:", error);
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `INV-${year}${month}-${random}`;
    }
  };

  // PDF Generation Function with Logo, Logs, Footers, and Payment Details
  const generateInvoicePDF = async (invoiceData, invoiceId) => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const html2canvas = (await import('html2canvas')).default;
      
      const invoiceContent = document.createElement('div');
      invoiceContent.style.position = 'absolute';
      invoiceContent.style.left = '-9999px';
      invoiceContent.style.width = '210mm';
      invoiceContent.style.padding = '8mm 10mm';
      invoiceContent.style.backgroundColor = 'white';
      invoiceContent.style.boxSizing = 'border-box';
      invoiceContent.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
      
      // Determine currency based on payment_mode
      const isBirr = invoiceData.payment_mode?.toLowerCase() === "birr" || 
                     invoiceData.payment_mode?.toLowerCase() === "etb";
      
      const formatCurrency = (amount) => {
        const formattedAmount = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount || 0);
        
        if (isBirr) {
          return `ETB ${formattedAmount}`;
        } else {
          return `$${formattedAmount}`;
        }
      };
      
      const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US').format(num || 0);
      };
      
      // Format unit price based on currency
      const formatUnitPrice = (price) => {
        const formattedPrice = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(price || 0);
        
        if (isBirr) {
          return formattedPrice; // Just the number, currency in header
        } else {
          return formattedPrice;
        }
      };
      
      console.log("INVOICE REPORT", invoiceData);
      const items = invoiceData.items || [];
      const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
      const taxAmount = subtotal * ((invoiceData.tax_rate || 0) / 100);
      const total = subtotal + taxAmount - (invoiceData.discount || 0) + (invoiceData.arrear || 0);
      
      // Get current date only (no time)
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
      // Set currency symbol for headers
      const currencySymbol = isBirr ? "ETB" : "$";
      
      invoiceContent.innerHTML = `
        <div style="position: relative; min-height: 287mm; display: flex; flex-direction: column;">
          <!-- HEADER SECTION with LOGO at TOP LEFT and DATE/INVOICE at TOP RIGHT -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 70px; padding-bottom: 15px; border-bottom: 1px solid #12151a;">
            <!-- Company Logo on Left -->
            <div style="display: flex; align-items: center;">
              <img src="${companyLogo}" alt="Company Logo" style="max-height: 70px; max-width: 200px; object-fit: contain;" 
                   onerror="this.onerror=null; this.style.display='none'; this.parentElement.innerHTML += '<h2 style=\'margin: 0; color: #1a365d; font-size: 24pt; font-weight: 700;\'>IPDC</h2>';">
            </div>
            
            <!-- DATE and INVOICE NUMBER at TOP RIGHT CORNER - Simplified -->
            <div style="text-align: right;">
              <p style="margin: 0 0 5px 0; font-size: 7pt; color: #000;">Date &nbsp; : &nbsp;&nbsp; <span style="font-weight: 100; color: #000; font-size: 7pt;">${currentDate}</span></p>
              <p style="margin: 0; font-size: 7pt; color: #000;">Invoice No &nbsp; : &nbsp;&nbsp; <span style="font-weight: 100; color: #000; font-size: 7pt;">${invoiceData.invoice_number || 'N/A'}</span></p>
            </div>
          </div>
          
          <!-- INVOICE TITLE -->
          <div style="text-align: center; margin-bottom: 30px;">
            <span style="color: #000; margin: 0 0 5px 0; font-size: 14pt; font-weight: 200; letter-spacing: 2px;">INVOICE</span>
          </div>
          
          <!-- INVESTOR INFORMATION - NO BOXES, JUST CLEAN TEXT -->
          <div style="margin-bottom: 10px;">
            <div style="border-bottom: 1px solid #12151a; margin-bottom: 20px;">
              <span style="color: #000; margin: 0 0 15px 0; border-bottom: 1px solid #e2e8f0; font-size: 10pt; font-weight: 200; letter-spacing: 2px;">INVESTOR INFORMATION</span>
            </div>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 5px 100px;">
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500; padding: 0 0 10px 0">
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    Investor Name: &nbsp; ${invoiceData.investor_name || 'N/A'}
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500;">
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    TIN No : &nbsp;${invoiceData.tin_no || 'N/A'}
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500;">
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    Payment Mode: &nbsp; ${invoiceData.payment_mode?.toUpperCase() || 'N/A'}
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight:500;">
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    Property Type : &nbsp; ${invoiceData.property_type || 'N/A'}
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500;">
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 7pt; font-weight: 200; letter-spacing: 1px;">
                    Park Name : &nbsp;${invoiceData.park_name || 'N/A'} 
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500;"> 
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    Property ID : &nbsp; ${invoiceData.property_id || 'N/A'} 
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500;"> 
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    Invoice Date : &nbsp; ${formatDate(invoiceData.invoice_date)}
                  </span>
                </div>
              </div>
              <div>
                <div style="font-size: 7pt; color: #000; font-weight: 500;">
                  <span style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px;">
                    Due Date &nbsp; : &nbsp;&nbsp;&nbsp; ${formatDate(invoiceData.due_date)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- INVOICE ITEMS -->
          <div style="margin-bottom: 30px; margin-top: 30px; border-bottom: 1px solid #12151a;">
            <span style="color: #000; margin: 0 0 5px 0; font-size: 10pt; font-weight: 200; letter-spacing: 2px;">INVOICE ITEMS</span>
            
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 8pt; border: 1px solid #12151a;">
              <thead>
                <tr style="background-color: #e1e4e7; color: white;">
                  <th style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px; text-align: left">Service</th>
                  <th style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px; text-align: left">Description</th>
                  <th style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px; text-align: center">Quantity</th>
                  <th style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px; text-align: right">Unit Price (${currencySymbol})</th>
                  <th style="color: #000; margin: 0 0 5px 0; font-size: 8pt; font-weight: 200; letter-spacing: 1px; text-align: right">Amount (${currencySymbol})</th>
                </tr>
              </thead>
              <tbody>
                ${items.map((item, index) => `
                  <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f8fafc'};">
                    <td style="padding: 3px; font-size: 8pt; color: #000; border-bottom: 1px solid #e2e8f0; font-weight: 200; letter-spacing: 1px; text-align: left">${item.name}</td>
                    <td style="padding: 5px; color: #000; font-size: 8pt; border-bottom: 1px solid #e2e8f0; font-weight: 200; letter-spacing: 1px; text-align: left">${item.description}</td>
                    <td style="padding: 5px; color: #000; font-size: 8pt; border-bottom: 1px solid #e2e8f0; text-align: center; font-weight: 200; letter-spacing: 1px;">${item.quantity || 0} </td>
                    <td style="padding: 5px; color: #000; font-size: 8pt; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: 200; letter-spacing: 1px;">${formatNumber(item.unit_price || 0)}</td>
                    <td style="padding: 5px; color: #000; font-size: 8pt; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: 200; letter-spacing: 1px;">${formatCurrency(item.amount || 0)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <!-- TOTALS SECTION - CLEAN RIGHT ALIGNED -->
            <div style="display: flex; justify-content: flex-end; margin-top: 10px;">
              <div style="width: 200px;">
                <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 7pt;">
                  <span style="color: #000; font-size: 8pt; font-weight: 100;">Subtotal:</span>
                  <span style="color: #000; font-size: 8pt; font-weight: 100;">${formatCurrency(subtotal)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 7pt;">
                  <span style="color: #000; font-size: 8pt; font-weight: 100;">Tax (${invoiceData.tax_rate || 0}%):</span>
                  <span style="color: #000; font-size: 8pt; font-weight: 100;">${formatCurrency(taxAmount)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 7pt;">
                  <span style="color: #000; font-size: 8pt; font-weight: 100;">Discount:</span>
                  <span style="color: #000; font-size: 8pt; font-weight: 100;">-${formatCurrency(invoiceData.discount || 0)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 7pt; padding-bottom: 10px; border-bottom: 1px dashed #12151a;">
                  <span style="color: #000; font-size: 8pt; font-weight: 100">Arrear:</span>
                  <span style="color: #000; font-size: 8pt; font-weight: 100">+${formatCurrency(invoiceData.arrear || 0)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; font-size: 5pt; font-weight: 100;">
                  <span style="color: #2fbc47; font-size: 10pt; font-weight: 100">TOTAL:</span>
                  <span style="color: #2fbc47; font-size: 10pt; font-weight: 100">${formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- FOOTER SECTION -->
          <div style="margin-top: auto; border-top: 1px solid #e2e8f0; padding-top: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <p style="margin: 0; font-weight: 200; font-size: 7pt; color: #000;">Industrial Park Development Corporation</p>
                <p style="margin: 5px 0 0 0; font-size: 7pt; font-weight: 200; color: #000;">LIS © ${new Date().getFullYear()} All Rights Reserved</p>
              </div>
              <div style="text-align: right; font-weight: 200; font-size: 7pt; color: #000;">
                This is a computer generated invoice. No signature required.
              </div>
            </div>
          </div>
        </div>
      `;
      
      document.body.appendChild(invoiceContent);
      
      const canvas = await html2canvas(invoiceContent, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: true
      });
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save(`Invoice_${invoiceData.invoice_number}.pdf`);
      
      document.body.removeChild(invoiceContent);
      showSuccessToast(`Invoice ${invoiceData.invoice_number} generated successfully!`);
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      showErrorToast("Failed to generate PDF invoice!");
    }
  };
  
  // Handle generate invoice
  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    
    if (!formData.investor_id) {
      showErrorToast("Please select an investor!");
      return;
    }
  
    try {
      setIsLoading(true);
      //showInfoToast("Generating invoice...");
      
      // Wait for invoice number generation if it's async
      const invoiceNumber = await generateInvoiceNumber();
      
      // Check payment mode and prepare currency prefix
      const isBirr = formData.payment_mode?.toLowerCase() === "birr" || formData.payment_mode?.toLowerCase() === "etb";
      const currencyPrefix = isBirr ? "ETB " : "$";
      
      // Now proceed with the rest of the logic
      const invoiceData = {
        ...formData,
        invoice_number: invoiceNumber,
        recorded_by: userValuesArray[1],
        // Add currency information to invoice data
        currency: isBirr ? "ETB" : "USD",
        currency_symbol: isBirr ? "ETB" : "$"
      };
      
      const itemsFlat = {};
      const invoiceitemlist = {
        etpCharges: 0, 
        managementService: 0, 
        stpCharges: 0, 
        waterService: 0,
        rentPayment: 0,
        landLeasedPayment: 0
      };
      
      invoiceData.items.forEach(item => {
        const key = item.name
          .toLowerCase()
          .replace(/\s+(.)/g, (match, group1) => group1.toUpperCase())
          .replace(/\s+/g, '')
          .replace(/[^\w]/g, '');
        
        // Store the amount with currency info in the itemsFlat
        // But keep the numeric value for calculations
        itemsFlat[key] = item.amount || 0;
        
        // You might want to store formatted amount for display
        itemsFlat[`${key}_formatted`] = isBirr 
          ? `ETB ${(item.amount || 0).toFixed(2)}` 
          : `$${(item.amount || 0).toFixed(2)}`;
      });
      
      Object.keys(invoiceitemlist).forEach(key => {
        if (key in itemsFlat) {
          invoiceitemlist[key] = itemsFlat[key];
        }
      });
      
      console.log(invoiceData);
      
      // Add currency information to the invoice data being sent to API
      const invoiceData2 = {
        ...invoiceData, 
        ...invoiceitemlist,
        // Add currency fields
        currency_code: isBirr ? "ETB" : "USD",
        currency_symbol: isBirr ? "ETB" : "$",
        // Format total amounts with currency for display if needed
        total_amount_display: isBirr 
          ? `ETB ${invoiceData.total_amount?.toFixed(2)}` 
          : `$${invoiceData.total_amount?.toFixed(2)}`,
        subtotal_display: isBirr 
          ? `ETB ${invoiceData.subtotal?.toFixed(2)}` 
          : `$${invoiceData.subtotal?.toFixed(2)}`
      };
      
      delete invoiceData2.property_id;
      delete invoiceData2.payment_records;
      delete invoiceData2.ownership_type;
      
      const invoiceData3 = { ...invoiceData2, items: formData.items }; 
      console.log("formData ZZZZZZZZZZZZZZZZZZZZZZZZZZZ:", invoiceData2);
      
      const result = await ExecutePostWithParams("finances/invoices", invoiceData2);
      
      if (result) {
        showSuccessToast(`Invoice ${invoiceNumber} generated successfully!`);
        
        // Pass currency info to PDF generation
        await generateInvoicePDF({
          ...invoiceData3,
          currency: isBirr ? "ETB" : "USD",
          currency_symbol: isBirr ? "ETB" : "$",
          isBirr: isBirr
        }, invoiceNumber);
        
        const resetFormData = getInitialInvoiceData(formData.ownership_type || "Residential");
        setFormData(resetFormData);
      }
      
    } catch (error) {
      console.error("Error generating invoice:", error);
      showErrorToast("Failed to generate invoice!");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit invoice (load into generate tab)
  const handleEditInvoice = (invoice) => {
    const baseItems = getItemsByPropertyType(invoice.property_type);
    
    const invoiceData = {
      ...invoice,
      items: baseItems.map(baseItem => {
        const invoiceItem = invoice.items?.find(item => item.id === baseItem.id);
        return invoiceItem ? invoiceItem : {
          ...baseItem,
          quantity: 0,
          unit_price: 0,
          amount: 0
        };
      })
    };
    
    setFormData(invoiceData);
    setMainTabValue(0);
    setViewDialogOpen(false);
    showInfoToast(`Editing invoice: ${invoice.invoice_number}`);
  };

  // Handle edit invoice from view dialog - this will enable inline editing
  const handleEditInvoiceFromDialog = (invoice) => {
    setViewDialogOpen(false);
    setMainTabValue(1); // Switch to Edit Invoice tab
    
    // Small delay to ensure tab switch happens before enabling edit mode
    setTimeout(() => {
      handleInlineEdit(invoice);
    }, 100);
    
    showInfoToast(`Editing invoice: ${invoice.invoice_number}`);
  };

  // Handle view invoice
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewDialogOpen(true);
    showInfoToast(`Viewing invoice: ${invoice.invoice_number}`);
  };
  
  const handleViewInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailFlag(true);
    showInfoToast(`Viewing invoice: ${invoice.invoice_number}`);
    console.log("INVOICE DETAIL", invoice)
  };

  // Handle add payment
  const handleAddPayment = () => {
    if (!selectedInvoice) return;
    
    const balanceDue = selectedInvoice.total_amount - (selectedInvoice?.payment_records?.reduce((sum, p) => sum + (p?.amount || 0), 0) || 0);
    
    setPaymentData({
      amount: balanceDue,
      payment_date: getTodayDate(),
      payment_method: "bank_transfer",
      transaction_id: `TRX-${Date.now()}`,
      reference: "",
      notes: `Payment for invoice ${selectedInvoice.invoice_number}`
    });
    
    setPaymentDialogOpen(true);
  };

  // Handle save payment
  const handleSavePayment = async () => {
    try {
      setIsLoading(true);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedInvoice = {
        ...selectedInvoice,
        payment_records: [
          ...(selectedInvoice?.payment_records || []),
          {
            id: `PAY-${Date.now()}`,
            ...paymentData,
            status: "completed"
          }
        ],
        status: "paid"
      };
      
      setSelectedInvoice(updatedInvoice);
      
      // Update in search results
      setSearchResults(prevResults => 
        prevResults.map(inv => 
          inv.id === updatedInvoice.id ? updatedInvoice : inv
        )
      );
      
      setPaymentDialogOpen(false);
      showSuccessToast("Payment recorded successfully!");
    } catch (error) {
      showErrorToast("Failed to record payment!");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete invoice
  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        setIsLoading(true);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setSearchResults(prev => prev.filter(inv => inv.id !== invoiceId));
        setTotalRecords(prev => prev - 1);
        
        if (selectedInvoice?.id === invoiceId) {
          setSelectedInvoice(null);
          setViewDialogOpen(false);
        }
        
        showSuccessToast("Invoice deleted successfully!");
      } catch (error) {
        showErrorToast("Failed to delete invoice!");
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle download invoice
  const handleDownloadInvoice = (invoice) => {
    generateInvoicePDF(invoice, invoice.invoice_number);
  };

  // Refresh activities list
  // const handleRefreshActivities = () => {
  //   setSearchQuery("");
  //   handleSearchInvoices();
  //   showInfoToast("Refreshing invoice list...");
  // };

  const fetchAgreementDetails = async () => {
    try {
      const agreementId = formData.agreement_id || (selectedAgreement && selectedAgreement.id);
      if (!agreementId) return;

      const resultData = await GetDataFromApiWithParams(`agreements/${agreementId}`);
      if (resultData && resultData.length > 0) {
        setAgreementDetail(resultData);
        setPaymentType(resultData[0].ownership_type === "rent" ? "Rental" : "Lease");
      }
    } catch (error) {
      console.error("Failed to fetch agreement details:", error);
    }
  };

  // Load agreements on component mount with default type
  useEffect(() => {
    fetchAgreementsByType(selectedAgreementType);
    setSearchResults(mockInvoices);
    setTotalRecords(mockInvoices.length);
  }, []); // Empty dependency array means this runs once on mount

  // Also refetch when selectedAgreementType changes via other means
  useEffect(() => {
    fetchAgreementsByType(selectedAgreementType);
  }, [selectedAgreementType]);

 // Load invoices on component mount
useEffect(() => {
  const fetchInvoices = async () => {
    try {
      setIsSearching(true);
      const response = await GetDataFromApiWithParams("finances/invoicesearch", { search: "" });
      console.log("All invoices on mount:", response);
      
      if (response) {
        let allInvoices = [];
        if (Array.isArray(response)) {
          allInvoices = response;
        } else if (response.data && Array.isArray(response.data)) {
          allInvoices = response.data;
        } else if (response.results && Array.isArray(response.results)) {
          allInvoices = response.results;
        }
        
        setAllInvoices(allInvoices);
        
        // Initially show all invoices
        setSearchResults(allInvoices);
        setTotalRecords(allInvoices.length);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setIsSearching(false);
    }
  };
  
  fetchInvoices();
}, []);

  // ==================== REAL-TIME CLIENT-SIDE SEARCH ====================
// Add this useEffect for real-time filtering as you type
useEffect(() => {
  // Don't filter if we're loading or no data
  if (isLoading || !allInvoices?.length) {
    return;
  }

  
  
  // If search is empty, show all records
  if (!searchQuery.trim()) {
    setSearchResults(allInvoices);
    setTotalRecords(allInvoices.length);
    return;
  }
  
  // Perform client-side filtering
  const searchValue = searchQuery.toLowerCase().trim();
  const filtered = allInvoices.filter(invoice => {
    // Search across multiple fields
    return (
      (invoice.invoice_num && invoice.invoice_num.toLowerCase().includes(searchValue)) ||
      (invoice.investor_name && invoice.investor_name.toLowerCase().includes(searchValue)) ||
      (invoice.investor_id && invoice.investor_id.toLowerCase().includes(searchValue)) ||
      (invoice.property_type && invoice.property_type.toLowerCase().includes(searchValue)) ||
      (invoice.status && invoice.status.toLowerCase().includes(searchValue))
    );
  });
  
  // Update search results and total records
  setSearchResults(filtered);
  setTotalRecords(filtered.length);
  
  // Reset to first page when filtering
  setPage(0);
  
}, [searchQuery, allInvoices, isLoading]); // Re-run when these dependencies change

  // Function to get color chip based on property type
  const getPropertyTypeColor = (propertyType) => {
    switch(propertyType) {
      case 'Shed Rental': return 'primary';
      case 'Developed Land Leased': return 'secondary';
      case 'Residential': return 'success';
      case 'Commercial': return 'warning';
      default: return 'default';
    }
  };
  
  const handleFocus = (e) => {
    e.target.select(); // Selects all text in the field
  };
  
  const handleBlur = (e) => {
    const { name, value } = e.target;
    // If empty on blur, set back to 0
    if (value === '' || value === null || value === undefined) {
      setFormData(prev => ({
        ...prev,
        [name]: 0
      }));
    } else if (value !== '' && (name === 'tax_rate' || name === 'discount' || name === 'arrear')) {
      // Ensure numeric fields are stored as numbers
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }));
    }
  };

  // Start inline editing - THIS IS THE FUNCTION THAT GETS CALLED WHEN EDIT ICON IS CLICKED
  const handleInlineEdit = (invoice) => {
    // If we're already editing a different row, cancel that edit first
    if (editingId && editingId !== invoice.id) {
      handleCancelInlineEdit();
    }
    
    setEditingId(invoice.id);
    setOriginalRowData({...invoice});
    setEditedRowData({...invoice});
    
    showInfoToast(`Editing invoice #${invoice.invoice_number}. Click save to save changes or cancel to discard.`);
  };

  // Save inline edits
  const handleSaveInlineEdit = async () => {
    if (!editedRowData) {
      showErrorToast("No data to save!");
      return;
    }
    
    try {
      setIsLoading(true);
      showInfoToast("Saving changes...");
      
      // Prepare the data for update
      const updateData = { ...editedRowData };
      
      // Remove unwanted fields if any
      const fieldsToRemove = ['payment_records', 'icon'];
      fieldsToRemove.forEach(field => delete updateData[field]);
      
      console.log("Updating invoice:", editingId, updateData);
      
      // Simulate API call - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update the search results locally
      setSearchResults(prevResults => 
        prevResults.map(record => 
          record.id === editingId ? { ...record, ...updateData } : record
        )
      );
      
      // If this is the selected invoice in view dialog, update it too
      if (selectedInvoice?.id === editingId) {
        setSelectedInvoice(prev => ({ ...prev, ...updateData }));
      }
      
      // Exit edit mode
      setEditingId(null);
      setEditedRowData(null);
      setOriginalRowData(null);
      
      showSuccessToast("Invoice updated successfully!");
    } catch (error) {
      console.error("Error saving invoice:", error);
      showErrorToast("Failed to save changes!");
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel inline editing
  const handleCancelInlineEdit = () => {
    setEditingId(null);
    setEditedRowData(null);
    setOriginalRowData(null);
    showInfoToast("Edit cancelled");
  };

  // Handle field changes in inline editing
  const handleFieldChange = (fieldName, value) => {
    setEditedRowData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };


  // ##############################
  // ==================== SEARCH FUNCTIONS ====================
const handleSearchKeyPress = (e) => {
  if (e.key === 'Enter') {
    handleSearchInvoices2();
  }
};

// const handleClearFilters = () => {
//   setSearchQuery("");
//   // Reset to show all invoices
//   setSearchResults(mockInvoices); // Replace with your actual data source
//   setTotalRecords(mockInvoices.length); // Replace with your actual data source
//   setPage(0);
//   showWarningToast("Search cleared");
// };

// Update your handleSearchInvoices function to include better feedback
// Update your handleSearchInvoices function to include better feedback
// Update your handleSearchInvoices function to include better feedback
const handleSearchInvoices2 = async (e) => {
  try {
    // Get the value from the event
    const type = e.target.value;
    
    setIsSearching(true);
    setSelectedAgreementType(type);
    
    console.log("Searching invoices type:", type);
    
    // Map the agreement type ID to the actual property_type value in the database
    let propertyTypeValue = "";
    
    switch(type) {
      case "Shed Rent":
        propertyTypeValue = "Shed Rent";
        break;
      case "Land Leased":
        propertyTypeValue = "Land Leased";
        break;
      case "residential":
        propertyTypeValue = "Residential";
        break;
      case "commercial":
        propertyTypeValue = "Commercial";
        break;
      case "all":
      default:
        propertyTypeValue = "";
        break;
    }
    
    console.log(`Mapped to property_type: "${propertyTypeValue}"`);
    
    // Fetch all invoices first
    const response = await GetDataFromApiWithParams("finances/invoicesearch", { search: "" });
    
    console.log("All invoices response:", response);
    
    if (response) {
      let allInvoices = [];
      if (Array.isArray(response)) {
        allInvoices = response;
      } else if (response.data && Array.isArray(response.data)) {
        allInvoices = response.data;
      } else if (response.results && Array.isArray(response.results)) {
        allInvoices = response.results;
      }
      
      // Filter by property_type if not 'all'
      let filteredResults = allInvoices;
      if (type !== 'all' && propertyTypeValue) {
        filteredResults = allInvoices.filter(invoice => {
          const invoiceType = invoice.property_type || '';
          return invoiceType.toLowerCase() === propertyTypeValue.toLowerCase();
        });
        console.log(`Filtered to ${filteredResults.length} invoices with property_type "${propertyTypeValue}"`);
      }
      
      // Store all invoices
      setAllInvoices(allInvoices);
      
      // Set search results
      setSearchResults(filteredResults);
      setTotalRecords(filteredResults.length);
      setPage(0);
      
      const resultCount = filteredResults.length;
      if (resultCount > 0) {
        const typeLabel = agreementTypes.find(t => t.id === type)?.label || type;
        // showSuccessToast(`Found ${resultCount} invoice(s) for ${typeLabel}`);
      } else {
        showWarningToast("No invoices found for your search.");
      }
    } else {
      setSearchResults([]);
      setTotalRecords(0);
      showWarningToast("No invoices found.");
    }
  } catch (error) {
    console.error("Error searching invoices:", error);
    setSearchResults([]);
    setTotalRecords(0);
    showErrorToast("Search failed! Please try again.");
  } finally {
    setIsSearching(false);
  }
};

// Handle agreement type change - this will trigger data fetch for both agreements and invoices
// Handle agreement type change - this will trigger data fetch for both agreements and invoices
// Handle agreement type change - this will trigger data fetch for both agreements and invoices
const handleAgreementTypeChange = async (e) => {
  const type = e.target.value;
  console.log("AGREEMENT TYPE SELECTED:", type);
  setSelectedAgreementType(type);
  
  // Clear search query when changing agreement type
  setSearchQuery("");
  
  // Reset investor selection when type changes
  setFormData(prev => ({
    ...prev,
    investor_id: "",
    investor_name: "",
    id: "",
    tin_no: "",
    payment_mode: "",
    ownership_type: type,
    park_name: ""
  }));
  
  // Fetch agreements for the selected type (for Generate Invoice tab)
  await fetchAgreementsByType(type);
  
  // Fetch invoices filtered by this agreement type (for Edit Invoice tab)
  await fetchInvoicesByAgreementType(type);
};

// New function to fetch invoices filtered by agreement type
// Updated function to fetch invoices filtered by agreement type
// Updated function to fetch invoices filtered by agreement type
// Function to fetch invoices filtered by agreement type
const fetchInvoicesByAgreementType = async (type) => {
  try {
    setIsSearching(true);
    
    console.log(`Fetching invoices for agreement type: ${type}`);
    
    let results = [];
    
    // If type is 'all', fetch all invoices
    if (type === 'all') {
      const response = await GetDataFromApiWithParams("finances/invoicesearch", { search: "" });
      
      if (response) {
        if (Array.isArray(response)) {
          results = response;
        } else if (response.data && Array.isArray(response.data)) {
          results = response.data;
        } else if (response.results && Array.isArray(response.results)) {
          results = response.results;
        }
      }
    } else {
      // For specific types, we need to filter by property_type
      // First, map the dropdown ID to the actual property_type value in the database
      let propertyTypeValue = "";
      
      // Map dropdown IDs to actual property_type values
      switch(type) {
        case "Shed Rent":
          propertyTypeValue = "Shed Rent";
          break;
        case "Land Leased":
          propertyTypeValue = "Land Leased";
          break;
        case "residential":
          propertyTypeValue = "Residential";
          break;
        case "commercial":
          propertyTypeValue = "Commercial";
          break;
        default:
          propertyTypeValue = type;
          break;
      }
      
      console.log(`Mapped to property_type: ${propertyTypeValue}`);
      
      // Fetch all invoices first, then filter client-side
      // This ensures we get all data even if the API doesn't support property_type filtering
      const response = await GetDataFromApiWithParams("finances/invoicesearch", { search: "" });
      
      if (response) {
        let allInvoices = [];
        if (Array.isArray(response)) {
          allInvoices = response;
        } else if (response.data && Array.isArray(response.data)) {
          allInvoices = response.data;
        } else if (response.results && Array.isArray(response.results)) {
          allInvoices = response.results;
        }
        
        // Filter client-side by property_type
        results = allInvoices.filter(invoice => {
          const invoiceType = invoice.property_type || '';
          return invoiceType.toLowerCase() === propertyTypeValue.toLowerCase();
        });
        
        console.log(`Found ${results.length} invoices with property_type "${propertyTypeValue}"`);
      }
    }
    
    console.log("Filtered invoices:", results);
    
    // Store all invoices (the filtered ones for this type)
    setAllInvoices(results);
    
    // Set search results (initially all filtered invoices)
    setSearchResults(results);
   
    setPage(0);
    
    if (type !== 'all') {
      const typeLabel = agreementTypes.find(t => t.id === type)?.label || type;
      if (results.length > 0) {
        // showSuccessToast(`Found ${results.length} invoice(s) for ${typeLabel}`);
      } else {
        showInfoToast(`No invoices found for ${typeLabel}`);
      }
    }
  } catch (error) {
    console.error("Error fetching invoices by agreement type:", error);
    showErrorToast("Failed to fetch invoices");
  } finally {
    setIsSearching(false);
  }
};

// Update handleSearchInvoices to respect the selected agreement type
// Improved search function that actually works
// Handle search invoices - This should ONLY show the list, not the details
const handleSearchInvoices = async () => {
  // Make sure we're in list view, not detail view
  setDetailFlag(false);
  setSelectedInvoice(null); // Clear selected invoice when doing a new search
  
  setIsSearching(true);
  
  try {
    // First, get all invoices if we don't have them
    let invoicesToSearch = allInvoices;
    
    // if (allInvoices.length === 0) {
      // Fetch all invoices if not loaded yet
      const response = await GetDataFromApiWithParams("finances/invoicesearch", { search: "" });
      
      if (response) {
        if (Array.isArray(response)) {
          invoicesToSearch = response;
        } else if (response.data && Array.isArray(response.data)) {
          invoicesToSearch = response.data;
        } else if (response.results && Array.isArray(response.results)) {
          invoicesToSearch = response.results;
        }
        setAllInvoices(invoicesToSearch);
      }
    // }
    
    // First filter by agreement type if not 'all'
    let filteredByType = invoicesToSearch;
    if (selectedAgreementType !== 'all') {
      filteredByType = filterInvoicesByType(invoicesToSearch, selectedAgreementType);
    }
    
    // If search query is empty, just show filtered by type
    if (!searchQuery.trim()) {
      setSearchResults(filteredByType);
      setTotalRecords(filteredByType.length);
      setPage(0);
      showInfoToast(`Showing all invoices (${filteredByType.length} records)`);
      setIsSearching(false);
      return;
    }
    
    // Apply search filter
    const searchValue = searchQuery.toLowerCase().trim();
    const filtered = filteredByType.filter(invoice => {
      return (
        (invoice.invoice_num && invoice.invoice_num.toLowerCase().includes(searchValue)) ||
        (invoice.investor_name && invoice.investor_name.toLowerCase().includes(searchValue)) ||
        (invoice.investor_id && invoice.investor_id.toLowerCase().includes(searchValue)) ||
        (invoice.property_type && invoice.property_type.toLowerCase().includes(searchValue)) ||
        (invoice.status && invoice.status.toLowerCase().includes(searchValue))
      );
    });
    
    setSearchResults(filtered);
    setTotalRecords(filtered.length);
    setPage(0);
    
    if (filtered.length > 0) {
      showSuccessToast(`Found ${filtered.length} invoice(s) matching "${searchQuery}"`);
    } else {
      showWarningToast(`No invoices found matching "${searchQuery}"`);
    }
  } catch (error) {
    console.error("Error searching invoices:", error);
    showErrorToast("Search failed! Please try again.");
  } finally {
    setIsSearching(false);
  }
};
// Update handleClearFilters to reset to agreement type filtered view
const handleClearFilters = () => {
  setSearchQuery("");
  // Reset to show invoices for the current agreement type
  fetchInvoicesByAgreementType(selectedAgreementType);
  showInfoToast("Search cleared");
};
const fetchInvoices = async () => {
  setIsSearching(true);
  try {
    const response = await GetDataFromApiWithParams("your-api-endpoint");
    if (response && Array.isArray(response)) {
      setAllInvoices(response); // Store in allInvoices
      setSearchResults(response); // Initially show all
      setTotalRecords(response.length);
    }
  } catch (error) {
    console.error("Error fetching invoices:", error);
  } finally {
    setIsSearching(false);
  }
};
// Update handleRefreshActivities to refresh with current agreement type
const handleRefreshActivities = () => {
  setSearchQuery("");
  setDetailFlag(false); // Reset to list view
  setSelectedInvoice(null); // Clear selected invoice
  // Refresh by fetching latest data and filtering by current agreement type
  fetchInvoicesByAgreementType(selectedAgreementType);
  showInfoToast("Refreshing invoice list...");
};
  // #############################
  // Render agreement type selector
  const renderAgreementTypeSelector = () => (
    <Grid item xs={12}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
           Agreement Type
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Agreement Type</InputLabel>
                <Select
                  value={selectedAgreementType}
                  onChange={handleAgreementTypeChange}
                  label="Agreement Type"
                >
                  {agreementTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isLoading && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">
                    Loading agreements...
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
  // Helper function to filter invoices by agreement type
// Helper function to filter invoices by agreement type
// Helper function to filter invoices by agreement type
const filterInvoicesByType = (invoices, type) => {
  if (!invoices || !Array.isArray(invoices)) return [];
  
  if (type === 'all') {
    return invoices;
  }
  
  // Map the selected type to match invoice property_type
  const typeMap = {
    'Shed Rent': 'Shed Rental',
    'Land Leased': 'Land Leased',
    'residential': 'Residential',
    'commercial': 'Commercial'
  };
  
  const mappedType = typeMap[type];
  console.log(`Filtering by mapped type: "${mappedType}" for selected type: "${type}"`);
  
  if (!mappedType) return invoices; // If no mapping, return all
  
  return invoices.filter(invoice => {
    const invoiceType = invoice.property_type || '';
    // Case-insensitive comparison
    return invoiceType.toLowerCase() === mappedType.toLowerCase();
  });
};

// Helper function to filter invoices by search query
// Helper function to filter invoices by search query
// Helper function to filter invoices by search query
const filterInvoices = (invoices, query) => {
  if (!invoices || !Array.isArray(invoices)) return [];
  
  if (!query.trim()) {
    // If no search query, return all invoices of current type
    return filterInvoicesByType(invoices, selectedAgreementType);
  }
  
  const searchValue = query.toLowerCase().trim();
  
  // First get invoices filtered by type
  const typeFilteredInvoices = filterInvoicesByType(invoices, selectedAgreementType);
  
  // Then apply search filter
  return typeFilteredInvoices.filter(invoice => {
    return (
      (invoice.invoice_num && invoice.invoice_num.toLowerCase().includes(searchValue)) ||
      (invoice.investor_name && invoice.investor_name.toLowerCase().includes(searchValue)) ||
      (invoice.investor_id && invoice.investor_id.toLowerCase().includes(searchValue)) ||
      (invoice.property_type && invoice.property_type.toLowerCase().includes(searchValue)) ||
      (invoice.status && invoice.status.toLowerCase().includes(searchValue))
    );
  });
};

  return (
    <Container maxWidth="xl">
      {/* Toast Notification */}
      <Snackbar 
        open={toastOpen} 
        autoHideDuration={6000} 
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleToastClose} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} className="no-print">
        <Tabs 
          value={mainTabValue} 
          onChange={(e, newValue) => setMainTabValue(newValue)}
          aria-label="invoice tabs"
          variant="fullWidth"
        >
          {mainTabs.map((label, index) => (
            <Tab 
              key={index}
              label={label} 
              id={`invoice-tab-${index}`}
              aria-controls={`invoice-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab 1: Generate Invoice */}
      <TabPanel value={mainTabValue} index={0}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 3 }}>
            Generate Invoice
          </Typography>
          
          <form onSubmit={handleGenerateInvoice}>
            <Grid container spacing={3}>
              {/* Agreement Type Selector */}
              {renderAgreementTypeSelector()}
              
              {/* Investor Information */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                      Investor Information
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Select Investor</InputLabel>
                          <Select
                            name="agreement_id"
                            value={formData.investor_id}
                            onChange={handleChange}
                            label="Select Investor"
                            disabled={isLoading || filteredInvestors.length === 0}
                          >
                            <MenuItem value="">Select Investor</MenuItem>
                            {filteredInvestors.map((investor) => (
                              <MenuItem key={investor.id} value={investor.id}>
                                {investor.company_name || investor.investor_name || investor.name} - {investor.park_name || investor.property_id} ({investor.ownership_type})
                              </MenuItem>
                            ))}
                          </Select>
                          {filteredInvestors.length === 0 && !isLoading && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                              No investors found for the selected agreement type
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                      
                      {formData.id && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Agreement ID"
                              value={formData.id}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="TIN No"
                              value={formData.tin_no}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Payment Mode"
                              value={formData.payment_mode}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                fullWidth
                                label="Property Type"
                                value={formData.ownership_type}
                                disabled
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TextField
                                fullWidth
                                label="Park Name"
                                value={formData.park_name}
                                disabled
                              />
                            </Box>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Invoice Details */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                      Invoice Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Invoice Date"
                          name="invoice_date"
                          type="date"
                          value={formData.invoice_date}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Due Date"
                          name="due_date"
                          type="date"
                          value={formData.due_date}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Tax Rate (%)"
                          name="tax_rate"
                          type="number"
                          value={formData.tax_rate}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          InputProps={{ endAdornment: '%' }}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Discount ($)"
                          name="discount"
                          type="number"
                          value={formData.discount}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Arrear ($)"
                          name="arrear"
                          type="number"
                          disabled
                          value={formData.arrear || 0}
                          onChange={handleChange}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="This amount will be ADDED to total"
                          sx={{
                            '& .MuiFormHelperText-root': {
                              color: 'warning.main',
                              fontWeight: 500
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Notes"
                          name="notes"
                          value={formData.notes}
                          onChange={handleChange}
                          multiline
                          rows={2}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

{/* Invoice Items */}
<Grid item xs={12}>
  <Card variant="outlined">
    <CardContent>
      <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
        Invoice Items for {formData.ownership_type || "Select Investor"}
      </Typography>
      
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell>Service</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Quantity/Area</TableCell>
              <TableCell align="center">
                Unit Price ({formData.payment_mode?.toLowerCase() === "usd" ? "$" : "ETB"})
              </TableCell>
              <TableCell align="center">
                Amount ({formData.payment_mode?.toLowerCase() === "usd" ? "$" : "ETB"})
              </TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {formData.items.map((item, index) => {
              // Determine if field should be editable
              // STP (index 1), ETP (index 2), Water Service (index 3) are editable
              const isEditable = index === 1 || index === 2 || index === 3;
              
              // Get investor data
              const selectedInvestor = Array.isArray(investors) 
                ? investors.find(inv => inv.id === formData.investor_id) 
                : null;
              
              // Determine if this is Residential or Commercial property type
              const isResidentialOrCommercial = 
                formData.ownership_type?.toLowerCase().includes('residential') || 
                formData.ownership_type?.toLowerCase().includes('commercial') ||
                formData.property_type?.toLowerCase().includes('residential') || 
                formData.property_type?.toLowerCase().includes('commercial');
              
              // Get values based on item type
              let quantityValue = item.quantity;
              let unitPriceValue = item.unit_price;
              let amountValue = item.amount;
              
              // For non-editable items, use the correct values from state/investor
              if (!isEditable) {
                if (index === 0) { // Rent Payment
                  if (isResidentialOrCommercial) {
                    // For Residential/Commercial, use monthly rent
                    quantityValue = 1;
                    unitPriceValue = parseFloat(selectedInvestor?.monthly_rent) || 0;
                    amountValue = quantityValue * unitPriceValue;
                    
                    // Update the item amount in formData if it's different
                    if (item.amount !== amountValue) {
                      setTimeout(() => {
                        const updatedItems = [...formData.items];
                        updatedItems[index].amount = amountValue;
                        updatedItems[index].quantity = quantityValue;
                        updatedItems[index].unit_price = unitPriceValue;
                        
                        const newSubtotal = updatedItems.reduce((sum, item) => sum + (item.amount || 0), 0);
                        const taxRate = parseFloat(formData.tax_rate) || 0;
                        const taxAmount = (newSubtotal * taxRate) / 100;
                        const discount = parseFloat(formData.discount) || 0;
                        const arrear = parseFloat(formData.arrear) || 0;
                        const total = newSubtotal + taxAmount - discount + arrear;
                        
                        setFormData(prev => ({
                          ...prev,
                          items: updatedItems,
                          subtotal: newSubtotal,
                          tax_amount: taxAmount,
                          total_amount: total
                        }));
                      }, 0);
                    }
                  } else {
                    // For Shed/Land, use area_on_deed
                    quantityValue = selectedInvestor?.area_on_deed || 1;
                    amountValue = quantityValue * unitPriceValue;
                  }
                } else if (index === 4) { // Management Service (only for Shed/Land)
                  if (!isResidentialOrCommercial) {
                    quantityValue = selectedInvestor?.area_on_deed || 1;
                    const annualFee = parseFloat(selectedInvestor?.annual_mngmnt_service_fee) || 0;
                    unitPriceValue = annualFee / 12;
                    amountValue = quantityValue * unitPriceValue;
                  }
                }
              }
              
              return (
                <TableRow key={item.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.icon}
                      {item.name}
                    </Box>
                  </TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      disabled={!isEditable}
                      value={quantityValue}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      sx={{ width: '80px' }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      disabled={!isEditable}
                      value={unitPriceValue}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      sx={{ width: '100px' }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 'bold' }}>
                      {formData.payment_mode?.toLowerCase() === "usd"
                        ? `$${safeToFixed(amountValue)}` 
                        : `${safeToFixed(amountValue)} ETB`}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Grid container spacing={2} sx={{ maxWidth: '400px' }}>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={6}>
            <Typography>Subtotal:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <Typography>
              {formData.payment_mode?.toLowerCase() === "usd" 
                ? `$${safeToFixed(formData.subtotal)}` 
                : `${safeToFixed(formData.subtotal)} ETB`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>Tax ({formData.tax_rate}%):</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <Typography>
              {formData.payment_mode?.toLowerCase() === "usd" 
                ? `$${safeToFixed(formData.tax_amount)}` 
                : `${safeToFixed(formData.tax_amount)} ETB`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>Discount:</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography sx={{ color: 'error.main' }}>
            <Typography sx={{ color: 'error.main' }}>
                {formData.payment_mode?.toLowerCase() === "usd" 
                  ? `-$${safeToFixed(formData.discount)}` 
                  : `-${safeToFixed(formData.discount)} ETB`}
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography sx={{ color: 'warning.main', fontWeight: 'bold' }}>
              Arrear (Added):
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography sx={{ color: 'warning.main', fontWeight: 'bold' }}>
            <Typography sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                {formData.payment_mode?.toLowerCase() === "usd" 
                  ? `+$${safeToFixed(formData.arrear || 0)}` 
                  : `+${safeToFixed(formData.arrear || 0)} ETB`}
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ borderColor: 'primary.main', borderWidth: 1 }} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Total Amount:
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {formData.payment_mode?.toLowerCase() === "usd" 
              ? `$${safeToFixed(formData.total_amount)}` 
              : `${safeToFixed(formData.total_amount)} ETB`}
          </Typography>
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </CardContent>
  </Card>
</Grid>
              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }} className="no-print">
                  <Button
                    type="submit"
                    variant="contained"
                    color="inherit"
                    size="large"
                    startIcon={<ReceiptIcon />}
                    disabled={isLoading || !formData.investor_id}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Record and Generate Invoice'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    sx={{
                      borderColor: '#1976d2',
                      color: '#1976d2',
                      '&:hover': {
                        borderColor: '#1565c0',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                    onClick={() => {
                      const resetFormData = getInitialInvoiceData(formData.ownership_type || "Residential");
                      setFormData(resetFormData);
                      setArrearInterest("0");
                      setTariff("1.5");
                    }}
                  >
                    Reset Form
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </TabPanel>

    
      {/* Tab 2: Edit Invoice */}
      <TabPanel value={mainTabValue} index={1}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} className="no-print">
            <Typography variant="h5" gutterBottom sx={{ color: '#1976d2' }}>
              Edit Invoices
            </Typography>
          </Box>
          
          {/* Agreement Type Selector */}
          <Paper sx={{ p: 3, mb: 3 }} className="no-print">
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Agreement Type</InputLabel>
                  <Select
                    value={selectedAgreementType}
                    onChange={handleAgreementTypeChange}
                    label="Agreement Type"
                    disabled={isLoading}
                  >
                    {agreementTypes.map(type => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {isLoading && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">
                      Loading invoices...
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            
            <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Search by Invoice Number, Investor Name, or ID"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    // Real-time filtering
                    if (allInvoices.length > 0) {
                      const filtered = filterInvoices(allInvoices, e.target.value);
                      setSearchResults(filtered);
                      setTotalRecords(filtered.length);
                      setPage(0);
                    }
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                    endAdornment: searchQuery && (
                      <IconButton size="small" onClick={() => {
                        setSearchQuery("");
                        // Reset to show all invoices of current type
                        const filtered = filterInvoicesByType(allInvoices, selectedAgreementType);
                        setSearchResults(filtered);
                        setTotalRecords(filtered.length);
                        setPage(0);
                      }}>
                        <ClearIcon />
                      </IconButton>
                    )
                  }}
                  variant="outlined"
                  disabled={isSearching}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRefreshActivities}
                  startIcon={<RefreshIcon />}
                  disabled={isSearching}
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handlePrintEditView}
                  startIcon={<PrintIcon />}
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  Print
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Results Table with ref */}
          <div ref={editViewRef}>
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice No.</TableCell>
                      <TableCell>Agreement</TableCell>
                      <TableCell>Property Type</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Total Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isSearching ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Loading invoices...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : searchResults.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="textSecondary">
                            {searchQuery 
                              ? 'No invoices found matching your search.' 
                              : 'No invoices found for the selected agreement type.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      searchResults.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((invoice) => {
                        const isEditing = editingId == invoice?.id;
                        return (
                          <TableRow 
                            key={invoice?.id} 
                            hover
                            sx={{ 
                              backgroundColor: isEditing ? 'action.selected' : 'inherit',
                              '&:hover': {
                                backgroundColor: isEditing ? 'action.selected' : 'action.hover'
                              }
                            }}
                          >
                            {/* Invoice Number */}
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editedRowData?.invoice_num || ''}
                                  onChange={(e) => handleFieldChange('invoice_num', e.target.value)}
                                  sx={{ width: '120px' }}
                                  inputProps={{ style: { fontSize: '0.875rem' } }}
                                />
                              ) : (
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {invoice?.invoice_num}
                                </Typography>
                              )}
                            </TableCell>
                            
                            {/* Investor */}
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editedRowData?.investor_name || ''}
                                  onChange={(e) => handleFieldChange('investor_name', e.target.value)}
                                  sx={{ width: '150px' }}
                                  inputProps={{ style: { fontSize: '0.875rem' } }}
                                />
                              ) : (
                                <>
                                  <Typography variant="body2">{invoice?.investor_name}</Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {invoice?.investor_id}
                                  </Typography>
                                </>
                              )}
                            </TableCell>
                            
                            {/* Property Type */}
                            <TableCell>
                              {isEditing ? (
                                <Select
                                  size="small"
                                  value={editedRowData?.property_type }
                                  onChange={(e) => handleFieldChange('property_type', e.target.value)}
                                  sx={{ width: '140px', fontSize: '0.875rem' }}
                                >
                                  <MenuItem value="Shed Rental">Shed Rental</MenuItem>
                                  <MenuItem value="Developed Land Leased">Developed Land Leased</MenuItem>
                                  <MenuItem value="Residential">Residential</MenuItem>
                                  <MenuItem value="Commercial">Commercial</MenuItem>
                                </Select>
                              ) : (
                                invoice?.property_type
                              )}
                            </TableCell>
                            
                            {/* Invoice Date */}
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  type="date"
                                  value={editedRowData?.invoice_date ? editedRowData?.invoice_date?.split('T')[0] : ''}
                                  onChange={(e) => handleFieldChange('invoice_date', e.target.value)}
                                  sx={{ width: '130px' }}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ style: { fontSize: '0.875rem' } }}
                                />
                              ) : (
                                formatDate(invoice?.invoice_date)
                              )}
                            </TableCell>
                            
                            {/* Due Date */}
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  type="date"
                                  value={editedRowData?.due_date ? editedRowData?.due_date.split('T')[0] : ''}
                                  onChange={(e) => handleFieldChange('due_date', e.target.value)}
                                  sx={{ width: '130px' }}
                                  InputLabelProps={{ shrink: true }}
                                  inputProps={{ style: { fontSize: '0.875rem' } }}
                                />
                              ) : (
                                formatDate(invoice?.due_date)
                              )}
                            </TableCell>
                            
                            {/* Total Amount */}
                            <TableCell>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  value={editedRowData?.total_amount || 0}
                                  onChange={(e) => handleFieldChange('total_amount', parseFloat(e.target.value) || 0)}
                                  sx={{ width: '100px' }}
                                  inputProps={{ min: 0, step: 0.01, style: { fontSize: '0.875rem' } }}
                                />
                              ) : (
                                <Typography sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                  ${safeToFixed(invoice?.total_amount)}
                                </Typography>
                              )}
                            </TableCell>
                            
                            {/* Status */}
                            <TableCell>
                              {isEditing ? (
                                <Select
                                  size="small"
                                  value={editedRowData?.status || 'pending'}
                                  onChange={(e) => handleFieldChange('status', e.target.value)}
                                  sx={{ width: '100px', fontSize: '0.875rem' }}
                                >
                                  <MenuItem value="pending">Pending</MenuItem>
                                  <MenuItem value="paid">Paid</MenuItem>
                                  <MenuItem value="overdue">Overdue</MenuItem>
                                </Select>
                              ) : (
                                invoice?.status?.toUpperCase()
                              )}
                            </TableCell>
                            
                            {/* Actions */}
                            <TableCell align="center">
                              <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                {isEditing ? (
                                  // Show Save and Cancel buttons when in edit mode
                                  <>
                                    <Tooltip title="Save Changes">
                                      <IconButton 
                                        size="small" 
                                        color="success"
                                        onClick={handleSaveInlineEdit}
                                        disabled={isLoading}
                                      >
                                        <SaveIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel Edit">
                                      <IconButton 
                                        size="small" 
                                        color="warning"
                                        onClick={handleCancelInlineEdit}
                                        disabled={isLoading}
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  // Show regular action buttons when not in edit mode
                                  <>
                                    <Tooltip title="View Details">
                                      <IconButton 
                                        size="small" 
                                        color="info"
                                        onClick={() => handleViewInvoice(invoice)}
                                      >
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Invoice">
                                      <IconButton 
                                        size="small" 
                                        color="primary"
                                        onClick={() => handleInlineEdit(invoice)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Invoice">
                                      <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={() => handleDeleteInvoice(invoice?.id)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Paper>
          </div>
        </Box>
      </TabPanel>

      {/* Tab 3: View Invoice & Payments */}
      <TabPanel value={mainTabValue} index={2}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} className="no-print">
            <Typography variant="h5" gutterBottom sx={{ color: '#000' }}>
              View Invoice & Payment Records
            </Typography>
          </Box>
          
          {/* Search Section */}
          <Paper sx={{ p: 3, mb: 3 }} className="no-print">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Search by Invoice Number, Investor Name, or ID"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchInvoices()}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  InputLabelProps={{
                    sx: {
                      fontSize: '20px',
                      '&.Mui-focused': {
                        fontSize: '20px',
                      },
                    },
                  }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  color="inherit"
                  onClick={handleSearchInvoices}
                  disabled={isSearching}
                  startIcon={<SearchIcon />}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRefreshActivities}
                  startIcon={<RefreshIcon />}
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </Paper>
          
          {/* search result - hidden when detailFlag is true */}
          {!detailFlag && (
            <div ref={searchResultsRef}>
              <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice No.</TableCell>
                        <TableCell>Agreement</TableCell>
                        <TableCell>Property Type</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Due Date</TableCell>
                        <TableCell>Total Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isSearching ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Loading invoices...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : searchResults.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No invoices found. Try searching or refresh.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        searchResults.map((invoice) => {

                          //console.log("INVOICE INVOICE INVOICE",invoice)
                          const isEditing = editingId === invoice?.invoice_num;
                          
                          return (
                            <TableRow 
                                key={invoice?.id} 
                                hover
                                sx={{ 
                                  backgroundColor: invoice?.payment_mode !== 'BIRR' 
                                    ? 'rgba(76, 175, 80, 0.1)'  // Light green background for USD rows
                                    : (isEditing ? 'action.selected' : 'inherit'),
                                  '&:hover': {
                                    backgroundColor: invoice?.payment_mode !== 'BIRR'
                                      ? 'rgba(76, 175, 80, 0.2)'  // Darker green on hover for USD rows
                                      : (isEditing ? 'action.selected' : 'action.hover')
                                  }
                                }}
                              >
                              {/* Invoice Number */}
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    value={editedRowData?.invoice_num || ''}
                                    onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
                                    sx={{ width: '120px' }}
                                    inputProps={{ style: { fontSize: '0.875rem' } }}
                                  />
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {invoice?.invoice_num}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* Investor */}
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    value={editedRowData?.investor_name || ''}
                                    onChange={(e) => handleFieldChange('investor_name', e.target.value)}
                                    sx={{ width: '150px' }}
                                    inputProps={{ style: { fontSize: '0.875rem' } }}
                                  />
                                ) : (
                                  <>
                                    <Typography variant="body1">{invoice?.investor_name}</Typography>
                                    <Typography variant="body1" >
                                      {invoice?.investor_id}
                                    </Typography>
                                  </>
                                )}
                              </TableCell>
                              
                              {/* Property Type */}
                              <TableCell>
                                {isEditing ? (
                                  <Select
                                    size="small"
                                    value={editedRowData?.property_type || 'Shed Rental'}
                                    onChange={(e) => handleFieldChange('property_type', e.target.value)}
                                    sx={{ width: '140px', fontSize: '0.875rem' }}
                                  >
                                    <MenuItem value="Shed Rental">Shed Rental</MenuItem>
                                    <MenuItem value="Developed Land Leased">Developed Land Leased</MenuItem>
                                    <MenuItem value="Residential">Residential</MenuItem>
                                    <MenuItem value="Commercial">Commercial</MenuItem>
                                  </Select>
                                ) : (
                                  <Typography variant="body1" display="block">
                                    {invoice?.property_type}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* Invoice Date */}
                              <TableCell>
                                {isEditing ? (
                                    <TextField
                                      size="small"
                                      type="date"
                                      value={editedRowData?.invoice_date ? editedRowData?.invoice_date?.split('T')[0] : ''}
                                      onChange={(e) => handleFieldChange('invoice_date', e.target.value)}
                                      sx={{ width: '130px' }}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ style: { fontSize: '0.875rem' } }}
                                    />
                                  ) : (
                                    <Typography 
                                      sx={{ 
                                        color: '#000080', 
                                        fontSize: '13px',
                                        fontWeight: 500
                                      }}
                                    >
                                      {formatDate(invoice?.invoice_date)}
                                    </Typography>
                                  )}
                              </TableCell>
                              
                              {/* Due Date */}
                              <TableCell>
                                {isEditing ? (
                                          <TextField
                                            size="small"
                                            type="date"
                                            value={editedRowData?.due_date ? editedRowData?.due_date.split('T')[0] : ''}
                                            onChange={(e) => handleFieldChange('due_date', e.target.value)}
                                            sx={{ width: '130px' }}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ style: { fontSize: '0.875rem' } }}
                                          />
                                        ) : (
                                          <Typography 
                                            sx={{ 
                                              color: '#000080', 
                                              fontSize: '13px',
                                              fontWeight: 500
                                            }}
                                          >
                                            {formatDate(invoice?.due_date)}
                                          </Typography>
                                        )}
                                 </TableCell>
                              
                              {/* Total Amount */}
                              <TableCell>
                                {isEditing ? (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedRowData?.total_amount || 0}
                                    onChange={(e) => handleFieldChange('total_amount', parseFloat(e.target.value) || 0)}
                                    sx={{ width: '100px' }}
                                    inputProps={{ min: 0, step: 0.01, style: { fontSize: '0.875rem' } }}
                                  />
                                ) : (
                                  <Typography sx={{ fontWeight: 'bold' }}>
                                    {invoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(invoice?.total_amount)} ETB`
                                      : `$${safeToFixed(invoice?.total_amount)}`}
                                  </Typography>
                                )}
                              </TableCell>
                              
                              {/* Status */}
                              <TableCell>
                                {isEditing ? (
                                    <Select
                                      size="small"
                                      value={editedRowData?.status || 'pending'}
                                      onChange={(e) => handleFieldChange('status', e.target.value)}
                                      sx={{ width: '100px', fontSize: '0.875rem' }}
                                    >
                                      <MenuItem value="pending">Pending</MenuItem>
                                      <MenuItem value="paid">Paid</MenuItem>
                                      <MenuItem value="overdue">Overdue</MenuItem>
                                    </Select>
                                  ) : (
                                    <Typography 
                                      sx={{ 
                                        color: '#000080', 
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        textTransform: 'capitalize'
                                      }}
                                    >
                                      {invoice?.status}
                                    </Typography>
                                  )}
                              </TableCell>
                              
                              {/* Actions */}
                              <TableCell align="center">
                                <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                  {isEditing ? (
                                    // Show Save and Cancel buttons when in edit mode
                                    <>
                                      <Tooltip title="Save Changes">
                                        <IconButton 
                                          size="small" 
                                          color="success"
                                          onClick={handleSaveInlineEdit}
                                          disabled={isLoading}
                                        >
                                          <SaveIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Cancel Edit">
                                        <IconButton 
                                          size="small" 
                                          color="warning"
                                          onClick={handleCancelInlineEdit}
                                          disabled={isLoading}
                                        >
                                          <CancelIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    // Show regular action buttons when not in edit mode
                                    <>
                                      <Tooltip title="View Details">
                                        <IconButton 
                                          size="small" 
                                          color="info"
                                          onClick={() => handleViewInvoiceDetail(invoice)}
                                        >
                                          <ViewIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={totalRecords}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </Paper>
            </div>
          )}

          {/* Display Selected Invoice Details with ref */}
          {detailFlag && selectedInvoice && (
            <Box>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }} className="no-print">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      Invoice No: {selectedInvoice?.invoice_num}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrintDetailView}
                        sx={{
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          '&:hover': {
                            borderColor: '#1565c0',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          },
                        }}
                      >
                        Print
                      </Button>
                      
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadInvoice(selectedInvoice)}
                        sx={{
                          borderColor: '#1976d2',
                          color: '#1976d2',
                          '&:hover': {
                            borderColor: '#1565c0',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                          },
                        }}
                      >
                        Download
                      </Button>
                      {selectedInvoice?.status === 'pending' && (
                        <Button
                          variant="contained"
                          color="inherit"
                          startIcon={<PaymentIcon />}
                          onClick={handleAddPayment}
                        >
                          Record Payment
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Printable detail view */}
                  <div ref={detailViewRef}>
                    <Grid container spacing={3}>
                      {/* Invoice Header */}
                      <Grid item xs={12}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ color: '#1976d2', mb: 1, fontSize: '14px' }}>
                              Investor Information
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                              <Typography sx={{ minWidth: '120px', fontSize: '14px', color: '#000000' }}>
                                Name:
                              </Typography>
                              <Typography sx={{ fontSize: '14px', color: '#000000', fontWeight: 'bold' }}>
                                {selectedInvoice?.investor_name}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                              <Typography sx={{ minWidth: '120px', fontSize: '14px', color: '#000000' }}>
                                ID:
                              </Typography>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                {selectedInvoice?.investor_id}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                              <Typography sx={{ minWidth: '120px', fontSize: '14px', color: '#000000' }}>
                                TIN No:
                              </Typography>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                {selectedInvoice?.tin_no || 'N/A'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                              <Typography sx={{ minWidth: '120px', fontSize: '14px', color: '#000000' }}>
                                Payment Mode:
                              </Typography>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                {selectedInvoice?.payment_mode || 'N/A'}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                              <Typography sx={{ minWidth: '120px', fontSize: '14px', color: '#000000' }}>
                                Park Name:
                              </Typography>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                {selectedInvoice?.park_name || 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ color: '#1976d2', mb: 1, fontSize: '14px' }}>
                              Invoice Details
                            </Typography>
                            
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                                  <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                    Invoice Date:
                                  </Typography>
                                  <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                    {formatDate(selectedInvoice?.invoice_date)}
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={6}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
                                  <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                    Due Date:
                                  </Typography>
                                  <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                    {formatDate(selectedInvoice?.due_date)}
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                                  <Typography sx={{ minWidth: '100px', fontSize: '14px', color: '#000000' }}>
                                    Property Type:
                                  </Typography>
                                  <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                    {selectedInvoice?.property_type}
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                                  <Typography sx={{ minWidth: '100px', fontSize: '14px', color: '#000000' }}>
                                    Property ID:
                                  </Typography>
                                  <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                    {selectedInvoice?.property_id}
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                  <Typography sx={{ minWidth: '100px', fontSize: '14px', color: '#000000' }}>
                                    Status: {selectedInvoice?.status}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Invoice Items */}
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2, fontSize: '16px' }}>
                          Invoice Items
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Service</TableCell>
                                <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Description</TableCell>
                                <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>Quantity</TableCell>
                                <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>Unit Price</TableCell>
                                <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedInvoice?.items && selectedInvoice?.items?.length > 0 ? (
                                selectedInvoice?.items?.map((item, index) => (
                                  <TableRow key={index}>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {item?.icon || <BusinessIcon sx={{ fontSize: '16px' }} />}
                                        <span style={{ fontSize: '14px', color: '#000000' }}>{item?.name}</span>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>{item?.description}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>{item?.quantity || 0}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>
                                     
                                      <Typography sx={{ fontWeight: 'bold' }}>
                                    {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(item?.unit_price)} ETB`
                                      : `$${safeToFixed(item?.unit_price)}`}
                                  </Typography>
                                      </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>
                                      <Typography sx={{ fontWeight: 'bold', fontSize: '14px', color: '#000000' }}>
                                        {/* ${safeToFixed(item?.amount)} */}
                                        {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(item?.amount)} ETB`
                                      : `$${safeToFixed(item?.amount)}`}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={5} align="center">
                                    <Typography sx={{ fontSize: '14px', color: '#000000' }}>No items found</Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>

                      {/* Totals */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          <Grid container spacing={1} sx={{ maxWidth: '400px' }}>
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>Subtotal:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                {/* ${safeToFixed(selectedInvoice?.subtotal)} */}
                                {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(selectedInvoice?.subtotal)} ETB`
                                      : `$${safeToFixed(selectedInvoice?.subtotal)}`}
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>Tax 
                              {/* ({selectedInvoice?.tax_rate || 0}%): */}
                              {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(selectedInvoice?.tax_rate)} ETB`
                                      : `$${safeToFixed(selectedInvoice?.tax_rate)}`}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                                
                                {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(selectedInvoice?.tax_amount)} ETB`
                                      : `$${safeToFixed(selectedInvoice?.tax_amount)}`}
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Typography sx={{ fontSize: '14px', color: '#000000' }}>Discount:</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ color: 'error.main', fontSize: '14px' }}>
                                {/* -${safeToFixed(selectedInvoice?.discount)} */}
                                {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `-${safeToFixed(selectedInvoice?.discount)} ETB`
                                      : `-$${safeToFixed(selectedInvoice?.discount)}`}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Typography sx={{ color: 'warning.main', fontWeight: 'bold', fontSize: '14px' }}>
                                Arrear (Added):
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography sx={{ color: 'warning.main', fontWeight: 'bold', fontSize: '14px' }}>
                                {/* +${safeToFixed(selectedInvoice?.arrear)} */}
                                {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `+${safeToFixed(selectedInvoice?.arrear)} ETB`
                                      : `+$${safeToFixed(selectedInvoice?.arrear)}`}

                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Divider sx={{ borderColor: 'primary.main', borderWidth: 1 }} />
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '14px', color: '#000000' }}>
                                Total Amount:
                              </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ textAlign: 'right' }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '14px' }}>
                                {/* ${safeToFixed(selectedInvoice?.total_amount)} */}

                                {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(selectedInvoice?.total_amount)} ETB`
                                      : `$${safeToFixed(selectedInvoice?.total_amount)}`}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* Payment Records */}
                      {selectedInvoice?.payment_records && selectedInvoice?.payment_records?.length > 0 ? (
                        <Grid item xs={12}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2, fontSize: '16px' }}>
                            Payment Records
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                  <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Payment ID</TableCell>
                                  <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Date</TableCell>
                                  <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>Amount</TableCell>
                                  <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Method</TableCell>
                                  <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Reference</TableCell>
                                  <TableCell sx={{ fontSize: '14px', color: '#000000' }}>Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {selectedInvoice?.payment_records?.map((payment, index) => (
                                  <TableRow key={index}>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>{payment?.id || 'N/A'}</TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>
                                      {formatDate(payment?.payment_date)}

                                      
                                      </TableCell>
                                    <TableCell align="right" sx={{ fontSize: '14px', color: '#000000' }}>
                                     
                                      {selectedInvoice?.payment_mode === 'BIRR' 
                                      ? `${safeToFixed(parseFloat(payment?.amount?.replace(/[^0-9.-]/g, '')) || 0)} ETB`
                                      : `$${safeToFixed(parseFloat(payment?.amount?.replace(/[^0-9.-]/g, '')) || 0)}`}
                                      </TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>
                                    {payment?.payment_method || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>{payment?.reference || 'N/A'}</TableCell>
                                    <TableCell sx={{ fontSize: '14px', color: '#000000' }}>
                                    {payment?.status || 'N/A'}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                  <TableCell colSpan={2}>
                                    <Typography sx={{ fontWeight: 'bold', fontSize: '14px', color: '#000000' }}>
                                      Total Paid:
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    
                                  <Typography sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '14px' }}>
                                  {(() => {
                                          if (!selectedInvoice?.payment_records) return "0.00";
                                          
                                          const total = selectedInvoice.payment_records.reduce((sum, p) => {
                                              const cleanAmount = String(p?.amount).replace(/[^0-9.-]/g, '');
                                              const amount = parseFloat(cleanAmount) || 0;
                                              return sum + amount;
                                          }, 0);
                                          
                                          const formattedTotal = total.toLocaleString('en-US', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2
                                          });
                                          
                                          // Check payment mode to determine currency suffix
                                          const paymentMode = selectedInvoice?.payment_mode?.toUpperCase();
                                          const isBirr = paymentMode === 'BIRR' || paymentMode === 'ETB';
                                          
                                          return isBirr 
                                              ? `${formattedTotal} ETB` 
                                              : `$${formattedTotal}`;
                                      })()}
                                  </Typography> 
                                          
                                  </TableCell>
                                  <TableCell colSpan={3}></TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      ) : (
                        <Grid item xs={12}>
                          <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2, fontSize: '16px' }}>
                            Payment Records
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                            <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                              No payment records found for this invoice
                            </Typography>
                          </Paper>
                        </Grid>
                      )}

                      {/* Notes */}
                      {selectedInvoice?.notes && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 2, fontSize: '14px', color: '#000000' }}>
                            Notes
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                            <Typography sx={{ fontSize: '14px', color: '#000000' }}>
                              {selectedInvoice?.notes}
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </div>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Display if no invoice selected */}
          {!selectedInvoice && searchResults.length === 0 && !detailFlag && (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <ReceiptIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No Invoice Selected
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Enter an invoice number above to view details and payment records
              </Typography>
            </Box>
          )}
        </Box>
      </TabPanel>

      {/* View Invoice Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Invoice Details -- Invoice No : {selectedInvoice?.invoice_num}
        </DialogTitle>
        <DialogContent dividers>
          {selectedInvoice ? (
            <Grid container spacing={1}>
              {/* Header Information */}
              <Grid container spacing={1} sx={{ pl: 2 }}>
                {/* Header Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                    Invoice Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Invoice Number:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedInvoice?.invoice_num || 'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Status:
                    </Typography>
                    <Chip 
                      label={selectedInvoice?.status?.toUpperCase() || 'N/A'} 
                      color={selectedInvoice?.status === 'paid' ? 'success' : 
                            selectedInvoice?.status === 'pending' ? 'warning' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>

                {/* Investor Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2 }}>
                    Investor Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Investor Name:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.investor_name || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Investor ID:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.investor_id || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      TIN No:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.tin_no || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Payment Mode:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.payment_mode || 'N/A'}</Typography>
                  </Box>
                </Grid>

                {/* Property Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2 }}>
                    Property Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Property Type:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.property_type || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Property ID:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.property_id || 'N/A'}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Park Name:
                    </Typography>
                    <Typography variant="body1">{selectedInvoice?.park_name || 'N/A'}</Typography>
                  </Box>
                </Grid>

                {/* Dates */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2 }}>
                    Dates
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Invoice Date:
                    </Typography>
                    <Typography variant="body1">{formatDate(selectedInvoice?.invoice_date)}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: '120px' }}>
                      Due Date:
                    </Typography>
                    <Typography variant="body1">{formatDate(selectedInvoice?.due_date)}</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Invoice Items */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2 }}>
                  Invoice Items
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>Service</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice?.items && selectedInvoice?.items?.length > 0 ? (
                        selectedInvoice?.items?.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {item?.icon || <BusinessIcon />}
                                {item?.type}
                              </Box>
                            </TableCell>
                            <TableCell>{item?.description || 'N/A'}</TableCell>
                            <TableCell align="right">{item?.quantity || 0}</TableCell>
                            <TableCell align="right">${safeToFixed(item?.unit_price)}</TableCell>
                            <TableCell align="right">${safeToFixed(item?.amount)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="textSecondary">No items found</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              {/* Totals */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Grid container spacing={2} sx={{ maxWidth: '400px' }}>
                    <Grid item xs={6}>
                      <Typography>Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography>${safeToFixed(selectedInvoice?.subtotal)}</Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography>Tax ({selectedInvoice?.tax_rate || 0}%):</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography>${safeToFixed(selectedInvoice?.tax_amount)}</Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography>Discount:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: 'error.main' }}>
                        -${safeToFixed(selectedInvoice?.discount)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                        Arrear (Added):
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                        +${safeToFixed(selectedInvoice?.arrear)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Divider sx={{ borderColor: 'primary.main', borderWidth: 1 }} />
                    </Grid>
                    
                    <Grid item xs={6}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Total Amount:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        ${safeToFixed(selectedInvoice?.total_amount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Notes */}
              {selectedInvoice?.payment_records?.[0]?.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ mt: 2 }}>
                    Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Typography variant="body2">
                      {selectedInvoice?.payment_records?.[0]?.notes}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Payment Records */}
              {selectedInvoice?.payment_records && selectedInvoice?.payment_records?.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', mt: 2 }}>
                    Payment Records
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>Payment ID</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Bank name</TableCell>
                          <TableCell>Method</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoice?.payment_records?.map((payment, index) => (
                          <TableRow key={index}>
                            <TableCell>{payment?.id || 'N/A'}</TableCell>
                            <TableCell>{formatDate(payment?.payment_date)}</TableCell>
                            <TableCell align="right">{payment?.amount}</TableCell>
                            <TableCell align="center">
                              {payment?.bank_name || 'N/A'}
                            </TableCell>
                            <TableCell>
                              {payment?.payment_method || 'N/A'}
                            </TableCell>
                            <TableCell>{payment?.reference || 'N/A'}</TableCell>
                            <TableCell>
                              {"PAID"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography color="textSecondary">No invoice selected</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {selectedInvoice && (
            <Button 
              variant="contained" 
              onClick={() => handleEditInvoiceFromDialog(selectedInvoice)}
              startIcon={<EditIcon />}
            >
              Edit Invoice
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: parseFloat(e.target.value) || 0})}
                InputProps={{ startAdornment: '$' }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentData.payment_method}
                  onChange={(e) => setPaymentData({...paymentData, payment_method: e.target.value})}
                  label="Payment Method"
                >
                  <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="check">Check</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction ID"
                value={paymentData.transaction_id}
                onChange={(e) => setPaymentData({...paymentData, transaction_id: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number"
                value={paymentData.reference}
                onChange={(e) => setPaymentData({...paymentData, reference: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={paymentData.notes}
                onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSavePayment} 
            variant="contained"
            disabled={!paymentData.amount || paymentData.amount <= 0}
          >
            Save Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}